import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Plus, User, FileText, ClipboardList, Pill, Activity, BookOpen, AlertTriangle, Phone, Mail, Pencil, Paperclip, Printer, Download, Trash2, Upload, Camera, Shield, ScanLine, CalendarCheck, FlaskConical, Archive, FileDown, Loader2, Zap, Clock, HeartPulse, Apple, ShieldAlert } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { toast } from 'sonner';
import FotoPazienteDialog from '@/components/FotoPazienteDialog';
import { AppuntamentiOspiteTab } from '@/components/AppuntamentiOspiteTab';
import { openPrintWindow } from '@/lib/letterhead';
import BarcodeScannerDialog from '@/components/BarcodeScannerDialog';
import ContrattoTab from '@/components/ContrattoTab';
import DocumentiIdentitaSection from '@/components/DocumentiIdentitaSection';
import ConfirmDialog from '@/components/ConfirmDialog';
import { autoDecodeCF } from '@/lib/cfDecoder';
import { compressImageForAI } from '@/lib/imageUtils';
import EmergencyPrintButton from '@/components/EmergencyPrintButton';
import FarmacoAutocomplete from '@/components/FarmacoAutocomplete';
import ScaleTab from '@/components/ScaleTab';
import EcgTab from '@/components/ospite/EcgTab';
import LabTab from '@/components/ospite/LabTab';
import NutrizioneTab from '@/components/ospite/NutrizioneTab';
import TrendPesoCard from '@/components/ospite/TrendPesoCard';
import AnalisiParametriDialog from '@/components/ospite/AnalisiParametriDialog';
import PAITab from '@/components/PAITab';
import RefertiTab from '@/components/RefertiTab';
import DictableTextarea from '@/components/DictableTextarea';
import QRCodeOspite from '@/components/QRCodeOspite';
import DatePickerField from '@/components/DatePickerField';

const DOSI_RAPIDE = ['1/4', '1/2', '3/4', '1', '1 e 1/2', '2', '3'];

const PRESIDI = ['Ossigenoterapia', 'Catetere vescicale', 'PEG', 'Sondino naso-gastrico', 'Tracheostomia',
  'Pacemaker', 'Defibrillatore impiantabile', 'Stomia', 'Presidi antidecubito', 'Carrozzina',
  'Deambulatore', 'Apparecchio acustico', 'Protesi dentaria', 'Occhiali', 'CPAP notturna', 'Pompa infusionale'];

const statusMap = { presente: 'bg-emerald-100 text-emerald-700', ricoverato: 'bg-amber-100 text-amber-700', dimesso: 'bg-slate-100 text-slate-600', deceduto: 'bg-gray-100 text-gray-500', trasferito: 'bg-blue-100 text-brand' };
const statusLabels = { presente: 'Presente', ricoverato: 'Ricoverato', dimesso: 'Dimesso', deceduto: 'Deceduto', trasferito: 'Trasferito' };

// === ANAGRAFICA TAB ===
function AnagraficaTab({ ospite, onUpdate }) {
  const { hasRole } = useAuth();
  const [editing, setEditing] = useState(false);
  const [showFoto, setShowFoto] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [form, setForm] = useState({});
  const [mmgList, setMmgList] = useState([]);

  const openEdit = () => {
    api.get('/utility/mmg').then(r => setMmgList(r.data || [])).catch(() => {});
    setForm({
      nome: ospite.nome || '', cognome: ospite.cognome || '', codice_fiscale: ospite.codice_fiscale || '',
      data_nascita: ospite.data_nascita || '', sesso: ospite.sesso || 'M', luogo_nascita: ospite.luogo_nascita || '',
      indirizzo_residenza: ospite.indirizzo_residenza || '', telefono: ospite.telefono || '',
      email: ospite.email || '', stanza: ospite.stanza || '', letto: ospite.letto || '',
      area: ospite.area || '',
      provenienza: ospite.provenienza || '', tipologia_retta: ospite.tipologia_retta || '',
      altezza_cm: ospite.altezza_cm || '',
      medico_base: ospite.medico_base || '', stato: ospite.stato || 'presente', note: ospite.note || '',
      consensi: ospite.consensi || { privacy: false, foto: false, trattamenti: false, trattamento_dati_sanitari: false, comunicazione_familiari: false, data_firma: '' },
      allergie: ospite.allergie || [],
      presidi: ospite.presidi || [],
      presidi_note: ospite.presidi_note || ''
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      await api.put(`/ospiti/${ospite.id}`, { ...form, altezza_cm: form.altezza_cm ? Number(form.altezza_cm) : null });
      toast.success('Anagrafica aggiornata');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore aggiornamento'); }
  };

  const handleFoto = async (photoBase64) => {
    try {
      await api.put(`/ospiti/${ospite.id}/foto`, { foto_base64: photoBase64 });
      toast.success('Foto aggiornata');
      if (onUpdate) onUpdate();
    } catch (e) { toast.error('Errore aggiornamento foto'); }
  };

  const handleDeleteFoto = async () => {
    if (!window.confirm('Sei sicuro di voler rimuovere la foto?')) return;
    try {
      await api.delete(`/ospiti/${ospite.id}/foto`);
      toast.success('Foto rimossa');
      if (onUpdate) onUpdate();
    } catch (e) { toast.error('Errore rimozione foto'); }
  };

  const handleScanResult = (result) => {
    setForm(f => ({
      ...f,
      codice_fiscale: result.codice_fiscale || f.codice_fiscale,
      data_nascita: result.data_nascita || f.data_nascita,
      sesso: result.sesso || f.sesso,
      luogo_nascita: result.luogo_nascita || f.luogo_nascita,
    }));
    const info = [];
    if (result.luogo_nascita) info.push(result.luogo_nascita);
    if (result.eta != null) info.push(`${result.eta} anni`);
    toast.success(`Dati compilati da CF${info.length ? ': ' + info.join(', ') : ''}`);
  };

  if (!ospite) return null;
  return (
    <div className="space-y-6" data-testid="anagrafica-tab">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          {/* Patient Photo */}
          <div className="relative group">
            <Avatar className="w-20 h-20 border-2 border-slate-200">
              {ospite.foto_base64 && <AvatarImage src={ospite.foto_base64} alt={`${ospite.nome} ${ospite.cognome}`} />}
              <AvatarFallback className={`text-xl font-bold ${ospite.sesso === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                {ospite.nome?.[0]}{ospite.cognome?.[0]}
              </AvatarFallback>
            </Avatar>
            {hasRole('coordinatore', 'amministrazione', 'medico', 'infermiere') && (
              <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white hover:bg-white/20" onClick={() => setShowFoto(true)} data-testid="edit-foto-btn">
                  <Camera className="w-4 h-4" />
                </Button>
                {ospite.foto_base64 && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white hover:bg-white/20" onClick={handleDeleteFoto} data-testid="delete-foto-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{ospite.cognome} {ospite.nome}</h2>
            <p className="text-sm text-slate-500 font-mono">{ospite.codice_fiscale || 'CF non inserito'}</p>
          </div>
        </div>
        {hasRole('coordinatore', 'amministrazione', 'medico') && (
          <Dialog open={editing} onOpenChange={setEditing}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" onClick={openEdit} data-testid="edit-anagrafica-btn"><Pencil className="w-4 h-4 mr-1" />Modifica</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Modifica Anagrafica</DialogTitle></DialogHeader>
              {/* Barcode scanner for edit */}
              <div className="pb-3 border-b">
                <Button variant="outline" size="sm" onClick={() => setShowScanner(true)} className="border-dashed border-blue-300 text-brand hover:bg-blue-50" data-testid="edit-scan-cf-btn">
                  <ScanLine className="w-4 h-4 mr-2" />Scansiona CF
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-xs">Nome</Label><Input data-testid="edit-nome" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Cognome</Label><Input data-testid="edit-cognome" value={form.cognome} onChange={e => setForm(f => ({ ...f, cognome: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={e => {
                  const val = e.target.value.toUpperCase();
                  setForm(f => ({ ...f, codice_fiscale: val }));
                  autoDecodeCF(val, (r) => {
                    setForm(f => ({ ...f, data_nascita: r.data_nascita || f.data_nascita, sesso: r.sesso || f.sesso, luogo_nascita: r.luogo_nascita || f.luogo_nascita }));
                    toast.success(`CF decodificato: ${r.luogo_nascita || ''}${r.eta != null ? `, ${r.eta} anni` : ''}`);
                  });
                }} className={form.codice_fiscale ? 'border-emerald-300' : ''} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Data Nascita</Label><DatePickerField value={form.data_nascita} onChange={v => setForm(f => ({ ...f, data_nascita: v }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Sesso</Label>
                  <Select value={form.sesso} onValueChange={v => setForm(f => ({ ...f, sesso: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="M">Maschio</SelectItem><SelectItem value="F">Femmina</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Luogo di Nascita</Label><Input value={form.luogo_nascita || ''} onChange={e => setForm(f => ({ ...f, luogo_nascita: e.target.value }))} placeholder="es. Roma (RM)" /></div>
                <div className="col-span-2 space-y-1.5"><Label className="text-xs">Indirizzo di Residenza</Label><Input value={form.indirizzo_residenza || ''} onChange={e => setForm(f => ({ ...f, indirizzo_residenza: e.target.value }))} placeholder="Via, Citta, CAP, Provincia" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Stato</Label>
                  <Select value={form.stato} onValueChange={v => setForm(f => ({ ...f, stato: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="presente">Presente</SelectItem><SelectItem value="ricoverato">Ricoverato</SelectItem><SelectItem value="dimesso">Dimesso</SelectItem><SelectItem value="deceduto">Deceduto</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Telefono</Label><Input value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Stanza</Label><Input value={form.stanza} onChange={e => setForm(f => ({ ...f, stanza: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Letto</Label><Input value={form.letto} onChange={e => setForm(f => ({ ...f, letto: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Area</Label>
                  <Select value={form.area} onValueChange={v => setForm(f => ({ ...f, area: v }))}><SelectTrigger><SelectValue placeholder="Seleziona area" /></SelectTrigger><SelectContent><SelectItem value="verde">Area Verde</SelectItem><SelectItem value="gialla">Area Gialla</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Provenienza</Label><Input value={form.provenienza} onChange={e => setForm(f => ({ ...f, provenienza: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Altezza (cm)</Label><Input type="number" value={form.altezza_cm} onChange={e => setForm(f => ({ ...f, altezza_cm: e.target.value }))} placeholder="es. 165" data-testid="altezza-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Tipologia Retta</Label><Input value={form.tipologia_retta} onChange={e => setForm(f => ({ ...f, tipologia_retta: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Medico di Base (MMG)</Label>
                  <Select value={form.medico_base || '_none'} onValueChange={v => setForm(f => ({ ...f, medico_base: v === '_none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Seleziona MMG" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Nessuno</SelectItem>
                      {mmgList.sort((a, b) => a.cognome.localeCompare(b.cognome)).map(m => (
                        <SelectItem key={m.id} value={`${m.cognome} ${m.nome}`}>{m.cognome} {m.nome}{m.telefono ? ` (${m.telefono})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5"><Label className="text-xs">Note</Label><DictableTextarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} /></div>
              </div>
              {/* Consensi Privacy Section */}
              <Separator className="my-3" />
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Shield className="w-3.5 h-3.5" />Consensi Privacy (GDPR)</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'privacy', label: 'Informativa Privacy' },
                    { key: 'foto', label: 'Consenso Fotografie' },
                    { key: 'trattamenti', label: 'Consenso Trattamenti' },
                    { key: 'trattamento_dati_sanitari', label: 'Trattamento Dati Sanitari' },
                    { key: 'comunicazione_familiari', label: 'Comunicazione ai Familiari' },
                  ].map(c => (
                    <label key={c.key} className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={form.consensi?.[c.key] || false}
                        onChange={e => setForm(f => ({ ...f, consensi: { ...f.consensi, [c.key]: e.target.checked } }))}
                        className="rounded border-slate-300"
                      />
                      <span className="text-xs text-slate-700">{c.label}</span>
                    </label>
                  ))}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Data Firma Consensi</Label>
                    <DatePickerField value={form.consensi?.data_firma || ''} onChange={v => setForm(f => ({ ...f, consensi: { ...f.consensi, data_firma: v } }))} />
                  </div>
                </div>
              </div>
              {/* Presidi e dispositivi in atto */}
              <Separator className="my-3" />
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" />Presidi e dispositivi in atto (stampati sulla scheda emergenza)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PRESIDI.map(p => (
                    <label key={p} className="flex items-center gap-2 p-1.5 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={(form.presidi || []).includes(p)}
                        onChange={e => setForm(f => ({
                          ...f,
                          presidi: e.target.checked ? [...(f.presidi || []), p] : (f.presidi || []).filter(x => x !== p)
                        }))}
                        className="rounded border-slate-300"
                        data-testid={`presidio-${p.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      />
                      <span className="text-[11px] text-slate-700">{p}</span>
                    </label>
                  ))}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Note sui presidi (es. ossigeno 2 l/min notturno)</Label>
                  <Input value={form.presidi_note || ''} onChange={e => setForm(f => ({ ...f, presidi_note: e.target.value }))} data-testid="presidi-note" />
                </div>
              </div>
              {/* Allergie / Intolleranze Farmacologiche */}
              <Separator className="my-3" />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-red-500" />Allergie / Intolleranze Farmacologiche</p>
                  <Button type="button" variant="ghost" size="sm" className="text-xs text-brand" onClick={() => setForm(f => ({ ...f, allergie: [...(f.allergie || []), { sostanza: '', reazione: '', gravita: 'moderata', categoria_atc: '' }] }))}>+ Aggiungi</Button>
                </div>
                {(form.allergie || []).map((a, i) => (
                  <AllergiaRow key={i} allergia={a} index={i} allergie={form.allergie} setForm={setForm} />
                ))}
                {(!form.allergie || form.allergie.length === 0) && <p className="text-xs text-slate-400 text-center py-2">Nessuna allergia registrata</p>}
              </div>
              <div className="flex justify-end gap-2 mt-4"><Button variant="outline" onClick={() => setEditing(false)}>Annulla</Button><Button data-testid="save-anagrafica" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">Salva Modifiche</Button></div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Photo Dialog */}
      <FotoPazienteDialog open={showFoto} onClose={() => setShowFoto(false)} onPhotoResult={handleFoto} currentPhoto={ospite.foto_base64} />
      {/* Barcode Scanner Dialog */}
      <BarcodeScannerDialog open={showScanner} onClose={() => setShowScanner(false)} onScanResult={handleScanResult} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><User className="w-4 h-4" />Dati Anagrafici</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><span className="text-xs text-slate-400 block">Nome</span><span className="font-medium text-slate-800">{ospite.nome}</span></div>
            <div><span className="text-xs text-slate-400 block">Cognome</span><span className="font-medium text-slate-800">{ospite.cognome}</span></div>
            <div><span className="text-xs text-slate-400 block">Codice Fiscale</span><span className="font-mono text-slate-800">{ospite.codice_fiscale || '-'}</span></div>
            <div><span className="text-xs text-slate-400 block">Data Nascita</span><span className="text-slate-800">{ospite.data_nascita ? new Date(ospite.data_nascita).toLocaleDateString('it-IT') : '-'}</span></div>
            <div><span className="text-xs text-slate-400 block">Sesso</span><span className="text-slate-800">{ospite.sesso === 'M' ? 'Maschio' : 'Femmina'}</span></div>
            <div><span className="text-xs text-slate-400 block">Luogo Nascita</span><span className="text-slate-800">{ospite.luogo_nascita || '-'}</span></div>
            <div><span className="text-xs text-slate-400 block">Indirizzo Residenza</span><span className="text-slate-800">{ospite.indirizzo_residenza || '-'}</span></div>
            <div><span className="text-xs text-slate-400 block">Medico di Base</span><span className="text-slate-800">{ospite.medico_base || '-'}</span></div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><ClipboardList className="w-4 h-4" />Dati Amministrativi</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><span className="text-xs text-slate-400 block">Data Ingresso</span><span className="text-slate-800">{ospite.data_ingresso ? new Date(ospite.data_ingresso).toLocaleDateString('it-IT') : '-'}</span></div>
            <div><span className="text-xs text-slate-400 block">Provenienza</span><span className="text-slate-800">{ospite.provenienza || '-'}</span></div>
            <div><span className="text-xs text-slate-400 block">Stanza/Letto</span><span className="text-slate-800">{ospite.stanza} - {ospite.letto}</span></div>
            <div><span className="text-xs text-slate-400 block">Area</span>
              {ospite.area ? <Badge className={`text-[10px] ${ospite.area === 'verde' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{ospite.area === 'verde' ? 'Area Verde' : 'Area Gialla'}</Badge> : <span className="text-slate-800">-</span>}
            </div>
            <div><span className="text-xs text-slate-400 block">Tipologia Retta</span><span className="text-slate-800">{ospite.tipologia_retta || '-'}</span></div>
          </div>
          {ospite.consensi && (
            <div className="mt-3 pt-3 border-t">
              <span className="text-xs text-slate-400 block mb-2 flex items-center gap-1"><Shield className="w-3 h-3" />Consensi Privacy</span>
              <div className="flex gap-2 flex-wrap">
                <Badge variant={ospite.consensi.privacy ? 'default' : 'outline'} className="text-[10px]">Privacy {ospite.consensi.privacy ? 'Si' : 'No'}</Badge>
                <Badge variant={ospite.consensi.foto ? 'default' : 'outline'} className="text-[10px]">Foto {ospite.consensi.foto ? 'Si' : 'No'}</Badge>
                <Badge variant={ospite.consensi.trattamenti ? 'default' : 'outline'} className="text-[10px]">Trattamenti {ospite.consensi.trattamenti ? 'Si' : 'No'}</Badge>
                <Badge variant={ospite.consensi.trattamento_dati_sanitari ? 'default' : 'outline'} className="text-[10px]">Dati Sanitari {ospite.consensi.trattamento_dati_sanitari ? 'Si' : 'No'}</Badge>
                <Badge variant={ospite.consensi.comunicazione_familiari ? 'default' : 'outline'} className="text-[10px]">Comunicazione Fam. {ospite.consensi.comunicazione_familiari ? 'Si' : 'No'}</Badge>
              </div>
              {ospite.consensi.data_firma && <p className="text-[10px] text-slate-400 mt-1">Firmato il: {new Date(ospite.consensi.data_firma).toLocaleDateString('it-IT')}</p>}
            </div>
          )}
        </CardContent>
      </Card>
      {/* Presidi e dispositivi */}
      {((ospite.presidi || []).length > 0 || ospite.presidi_note) && (
        <Card className="lg:col-span-2" data-testid="presidi-card">
          <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-slate-600" />Presidi e dispositivi in atto</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {(ospite.presidi || []).map((p, i) => (
                <Badge key={i} variant="outline" className="text-[11px]">{p}</Badge>
              ))}
            </div>
            {ospite.presidi_note && <p className="text-xs text-slate-500 mt-2">{ospite.presidi_note}</p>}
          </CardContent>
        </Card>
      )}
      {/* Allergie display */}
      {ospite.allergie && ospite.allergie.length > 0 && (
        <Card className="lg:col-span-2 border-red-200 bg-red-50/30">
          <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-700"><AlertTriangle className="w-4 h-4" />Allergie / Intolleranze Farmacologiche</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {ospite.allergie.map((a, i) => (
                <div key={i} className={`flex items-start gap-3 p-2.5 rounded-lg border ${a.gravita === 'grave' ? 'bg-red-50 border-red-300' : a.gravita === 'moderata' ? 'bg-orange-50 border-orange-200' : 'bg-amber-50 border-amber-200'}`}>
                  <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${a.gravita === 'grave' ? 'text-red-600' : 'text-orange-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800">{a.sostanza}</span>
                      <Badge className={`text-[9px] ${a.gravita === 'grave' ? 'bg-red-200 text-red-800' : a.gravita === 'moderata' ? 'bg-orange-200 text-orange-800' : 'bg-amber-200 text-amber-800'}`}>{a.gravita}</Badge>
                    </div>
                    {a.categoria_atc && <p className="text-[11px] text-slate-500 mt-0.5">Categoria: <span className="font-medium text-slate-700">{a.categoria_atc_nome || a.categoria_atc}</span></p>}
                    {a.reazione && <p className="text-[11px] text-red-600 mt-0.5">Reazione: {a.reazione}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {ospite.referenti?.length > 0 && (
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Referenti Familiari</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ospite.referenti.map((r, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{r.nome} <span className="text-xs text-slate-400">({r.parentela})</span></p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      {r.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.telefono}</span>}
                      {r.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <div className="lg:col-span-2">
        <DocumentiIdentitaSection ospiteId={ospite.id} />
      </div>
    </div>
    </div>
  );
}

// === CARTELLA CLINICA TAB ===
function CartellaTab({ ospiteId }) {
  const { hasRole } = useAuth();
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tipo: 'diagnosi_principale', contenuto: '', note: '' });
  const tipi = { diagnosi_principale: 'Diagnosi Principale', diagnosi_secondaria: 'Diagnosi Secondaria', anamnesi_remota: 'Anamnesi Remota', anamnesi_prossima: 'Anamnesi Prossima', allergia: 'Allergia', terapia_cronica: 'Terapia Cronica' };

  const fetchRecords = useCallback(() => {
    api.get(`/ospiti/${ospiteId}/cartella`).then(r => setRecords(r.data)).catch(() => {});
  }, [ospiteId]);
  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSave = async () => {
    if (!form.contenuto) { toast.error('Inserisci il contenuto'); return; }
    try { await api.post(`/ospiti/${ospiteId}/cartella`, form); toast.success('Record aggiunto'); setShowForm(false); setForm({ tipo: 'diagnosi_principale', contenuto: '', note: '' }); fetchRecords(); } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  return (
    <div className="space-y-4" data-testid="cartella-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-slate-700">Cartella Clinica</h3>
        {hasRole('medico', 'coordinatore', 'infermiere') && (
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-cartella-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-1" />Aggiungi</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuovo Record Clinico</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="text-xs">Tipo</Label>
                  <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(tipi).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">Contenuto *</Label><DictableTextarea data-testid="cartella-contenuto" value={form.contenuto} onChange={e => setForm(f => ({ ...f, contenuto: e.target.value }))} rows={4} /></div>
                <div><Label className="text-xs">Note</Label><DictableTextarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} placeholder="Note aggiuntive..." /></div>
                <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button><Button data-testid="save-cartella" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">Salva</Button></div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {records.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Nessun record clinico</p> : (
        <div className="space-y-3">
          {records.map(r => (
            <Card key={r.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="text-[10px] mb-2">{tipi[r.tipo] || r.tipo}</Badge>
                    <p className="text-sm text-slate-800">{r.contenuto}</p>
                    {r.note && <p className="text-xs text-slate-400 mt-1">Note: {r.note}</p>}
                  </div>
                  <div className="text-right text-xs text-slate-400 flex-shrink-0 ml-4">
                    <p>{r.created_by_name}</p>
                    <p>{new Date(r.created_at).toLocaleDateString('it-IT')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// === TERAPIE TAB ===
function TerapieOspiteTab({ ospiteId }) {
  const { hasRole } = useAuth();
  const [terapie, setTerapie] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [alertAllergie, setAlertAllergie] = useState([]);
  const [rifornimentoId, setRifornimentoId] = useState(null);
  const [rifQta, setRifQta] = useState('');
  const [form, setForm] = useState({ farmaco: '', dosaggio: '', via: 'orale', orari: [], al_bisogno: false, durata_inizio: new Date().toISOString().slice(0, 10), durata_fine: 'mai', note: '', principio_attivo: '', confezione_unita: '', scorta_attuale: '', soglia_riordino: '7', giorni_settimana: ['lun','mar','mer','gio','ven','sab','dom'], schema_giorni: 'tutti', dosaggi_per_orario: {}, dosaggio_diverso: false });
  // Photo scan state
  const [scanning, setScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const fileInputRef = useRef(null);
  // Somministra Ora dialog
  const [sommDialog, setSommDialog] = useState(null);
  const [sommNota, setSommNota] = useState('');
  // Storico terapie
  const [storico, setStorico] = useState([]);
  const [showStorico, setShowStorico] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const ORARI_PRESET = ['06:00','07:00','08:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','18:00','19:00','20:00','21:00','22:00'];
  const GIORNI = [
    { key: 'lun', label: 'Lun' }, { key: 'mar', label: 'Mar' }, { key: 'mer', label: 'Mer' },
    { key: 'gio', label: 'Gio' }, { key: 'ven', label: 'Ven' }, { key: 'sab', label: 'Sab' }, { key: 'dom', label: 'Dom' }
  ];
  const toggleOrario = (o) => setForm(f => ({ ...f, orari: f.orari.includes(o) ? f.orari.filter(x => x !== o) : [...f.orari, o].sort() }));
  const toggleGiorno = (g) => setForm(f => ({ ...f, giorni_settimana: f.giorni_settimana.includes(g) ? f.giorni_settimana.filter(x => x !== g) : [...f.giorni_settimana, g] }));
  const setSchemaGiorni = (schema) => {
    if (schema === 'tutti') setForm(f => ({ ...f, schema_giorni: 'tutti', giorni_settimana: ['lun','mar','mer','gio','ven','sab','dom'] }));
    else if (schema === 'alterni_pari') setForm(f => ({ ...f, schema_giorni: 'alterni_pari', giorni_settimana: ['lun','mer','ven','dom'] }));
    else if (schema === 'alterni_dispari') setForm(f => ({ ...f, schema_giorni: 'alterni_dispari', giorni_settimana: ['mar','gio','sab'] }));
    else setForm(f => ({ ...f, schema_giorni: 'personalizzato' }));
  };

  const fetchTerapie = useCallback(() => {
    api.get('/terapie', { params: { ospite_id: ospiteId } }).then(r => setTerapie(r.data)).catch(() => {});
    api.get(`/terapie/storico/${ospiteId}`).then(r => setStorico(r.data)).catch(() => {});
  }, [ospiteId]);
  useEffect(() => { fetchTerapie(); }, [fetchTerapie]);

  const openEditDialog = (t) => {
    const dpo = t.dosaggi_per_orario || [];
    const dpoMap = {};
    dpo.forEach(d => { dpoMap[d.orario] = d.quantita; });
    const hasDifferent = dpo.length > 1 && new Set(dpo.map(d => d.quantita)).size > 1;
    setEditForm({
      dosaggio: t.dosaggio || '',
      via: t.via_somministrazione || t.via || 'Orale',
      orari: t.orari || [],
      al_bisogno: t.al_bisogno || false,
      note: t.note || '',
      durata_fine: t.durata_fine || 'mai',
      giorni_settimana: t.giorni_settimana || ['lun','mar','mer','gio','ven','sab','dom'],
      schema_giorni: t.schema_giorni || 'tutti',
      dosaggio_diverso: hasDifferent,
      dosaggi_per_orario: dpoMap,
      nota_cambio: '',
    });
    setEditDialog(t);
  };

  const handleEditSave = async () => {
    if (!editDialog || !editForm) return;
    try {
      let dosaggio = editForm.dosaggio;
      let dosaggiPerOrario = [];
      if (editForm.dosaggio_diverso && editForm.orari.length > 0) {
        dosaggiPerOrario = editForm.orari.map(o => ({
          orario: o,
          quantita: editForm.dosaggi_per_orario[o] || editForm.dosaggio || '1 compressa'
        }));
        const quantities = dosaggiPerOrario.map(d => d.quantita);
        if (new Set(quantities).size > 1) {
          dosaggio = dosaggiPerOrario.map(d => `${d.quantita} (${d.orario})`).join(', ');
        } else {
          dosaggio = quantities[0] || editForm.dosaggio;
        }
      }
      const payload = {
        dosaggio,
        dosaggi_per_orario: dosaggiPerOrario.length > 0 ? dosaggiPerOrario : editForm.orari.map(o => ({ orario: o, quantita: dosaggio })),
        via: editForm.via,
        orari: editForm.al_bisogno ? [] : editForm.orari,
        al_bisogno: editForm.al_bisogno,
        note: editForm.note,
        durata_fine: editForm.durata_fine === 'mai' ? '' : editForm.durata_fine,
        giorni_settimana: editForm.giorni_settimana,
        schema_giorni: editForm.schema_giorni,
        nota_cambio: editForm.nota_cambio,
      };
      await api.put(`/terapie/${editDialog.id}`, payload);
      toast.success('Terapia modificata');
      setEditDialog(null);
      setEditForm(null);
      fetchTerapie();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nella modifica'); }
  };

  const toggleEditOrario = (o) => setEditForm(f => ({ ...f, orari: f.orari.includes(o) ? f.orari.filter(x => x !== o) : [...f.orari, o].sort() }));
  const toggleEditGiorno = (g) => setEditForm(f => ({ ...f, giorni_settimana: f.giorni_settimana.includes(g) ? f.giorni_settimana.filter(x => x !== g) : [...f.giorni_settimana, g] }));
  const setEditSchema = (schema) => {
    if (schema === 'tutti') setEditForm(f => ({ ...f, schema_giorni: 'tutti', giorni_settimana: ['lun','mar','mer','gio','ven','sab','dom'] }));
    else if (schema === 'alterni_pari') setEditForm(f => ({ ...f, schema_giorni: 'alterni_pari', giorni_settimana: ['lun','mer','ven','dom'] }));
    else if (schema === 'alterni_dispari') setEditForm(f => ({ ...f, schema_giorni: 'alterni_dispari', giorni_settimana: ['mar','gio','sab'] }));
    else setEditForm(f => ({ ...f, schema_giorni: 'personalizzato' }));
  };

  const checkAllergie = async (farmaco, pa) => {
    if (!farmaco && !pa) { setAlertAllergie([]); return; }
    try {
      const res = await api.get(`/terapie/check-allergie/${ospiteId}`, { params: { farmaco, principio_attivo: pa } });
      setAlertAllergie(res.data.alerts || []);
    } catch { setAlertAllergie([]); }
  };

  const handleDrugSelect = (nome, pa) => {
    setForm(f => ({ ...f, farmaco: nome, principio_attivo: pa || '' }));
    checkAllergie(nome, pa || '');
  };

  // Auto-detect via and dosaggio unit from AIFA forma farmaceutica
  const detectViaAndUnit = (forme) => {
    if (!forme || forme.length === 0) return { via: 'orale', unitLabel: 'compressa', placeholder: 'es. 1 compressa' };
    const f = forme[0].toLowerCase();
    const raw = forme[0]; // original case for display

    // Extract specific form name from AIFA text
    const specificUnit = (base, fallback) => {
      // Clean up: "COMPRESSA EFFERVESCENTE" → "compressa effervescente"
      const clean = raw.toLowerCase()
        .replace(/\s+per uso\s+\w+/gi, '')
        .replace(/\s+in\s+(blister|flacone|tubo|fiala)\b.*/gi, '')
        .trim();
      // If original has useful qualifiers, use them
      if (clean.length > base.length + 2 && clean.startsWith(base)) return clean;
      return fallback || base;
    };

    // Iniettabili
    if (f.includes('iniettabile') || f.includes('infusione') || f.includes('siringa') || f.includes('penna pre-riempita') || f.includes('cartuccia')) {
      if (f.includes('sottocutan') || f.includes('penna')) return { via: 'sottocutanea', unitLabel: f.includes('penna') ? 'dose penna' : 'fiala', placeholder: 'es. 1 fiala' };
      if (f.includes('endoven') || f.includes('infusione')) return { via: 'endovenosa', unitLabel: 'fiala', placeholder: 'es. 1 fiala in 100ml SF' };
      return { via: 'intramuscolare', unitLabel: 'fiala', placeholder: 'es. 1 fiala' };
    }
    // Compresse e confetti
    if (f.includes('compressa') || f.includes('confetto')) {
      let unit = 'compressa';
      if (f.includes('effervescen')) unit = 'compressa effervescente';
      else if (f.includes('orodispersi') || f.includes('orosolubil') || f.includes('dispersibil')) unit = 'compressa dispersibile';
      else if (f.includes('masticabil')) unit = 'compressa masticabile';
      else if (f.includes('rilascio modificato')) unit = 'compressa a rilascio modificato';
      else if (f.includes('rilascio prolungato')) unit = 'compressa a rilascio prolungato';
      else if (f.includes('gastroresisten') || f.includes('enteric')) unit = 'compressa gastroresistente';
      else if (f.includes('rivestit')) unit = 'compressa rivestita';
      else if (f.includes('sublinguale') || f.includes('buccale')) { unit = 'compressa sublinguale'; return { via: 'sublinguale', unitLabel: unit, placeholder: `es. 1 ${unit}` }; }
      else if (f.includes('vaginale')) { unit = 'compressa vaginale'; return { via: 'altro', unitLabel: unit, placeholder: `es. 1 ${unit}` }; }
      else if (f.includes('confetto')) unit = 'confetto';
      return { via: 'orale', unitLabel: unit, placeholder: `es. 1 ${unit}` };
    }
    // Capsule
    if (f.includes('capsula')) {
      let unit = 'capsula';
      if (f.includes('molle') || f.includes('molli')) unit = 'capsula molle';
      else if (f.includes('rigida') || f.includes('rigide')) unit = 'capsula rigida';
      else if (f.includes('gastroresisten')) unit = 'capsula gastroresistente';
      else if (f.includes('rilascio modificato')) unit = 'capsula a rilascio modificato';
      else if (f.includes('rilascio prolungato')) unit = 'capsula a rilascio prolungato';
      if (f.includes('vaginale')) return { via: 'altro', unitLabel: 'capsula vaginale', placeholder: 'es. 1 capsula vaginale' };
      return { via: 'orale', unitLabel: unit, placeholder: `es. 1 ${unit}` };
    }
    // Forme liquide orali
    if (f.includes('sciroppo')) return { via: 'orale', unitLabel: 'ml di sciroppo', placeholder: 'es. 5 ml' };
    if (f.includes('gocce orali') || f.includes('gocce per')) return { via: 'orale', unitLabel: 'gocce', placeholder: 'es. 10 gocce' };
    if (f.includes('sospensione orale') || f.includes('soluzione orale')) return { via: 'orale', unitLabel: 'ml', placeholder: 'es. 5 ml' };
    if (f.includes('granulato effervescen')) return { via: 'orale', unitLabel: 'bustina effervescente', placeholder: 'es. 1 bustina' };
    if (f.includes('granulato') || f.includes('polvere per soluzione orale') || f.includes('bustina')) return { via: 'orale', unitLabel: 'bustina', placeholder: 'es. 1 bustina' };
    // Supposte
    if (f.includes('supposta')) return { via: 'rettale', unitLabel: 'supposta', placeholder: 'es. 1 supposta' };
    if (f.includes('sospensione rettale') || f.includes('rettale')) return { via: 'rettale', unitLabel: 'dose rettale', placeholder: 'es. 1 microclisma' };
    // Inalazione
    if (f.includes('inalazi') || f.includes('nebulizz') || f.includes('aerosol') || f.includes('polvere per inalaz')) return { via: 'inalatoria', unitLabel: 'puff', placeholder: 'es. 2 puff' };
    // Topico
    if (f.includes('crema')) return { via: 'topica', unitLabel: 'applicazione di crema', placeholder: 'es. 1 applicazione' };
    if (f.includes('gel')) return { via: 'topica', unitLabel: 'applicazione di gel', placeholder: 'es. 1 applicazione' };
    if (f.includes('pomata') || f.includes('unguento')) return { via: 'topica', unitLabel: 'applicazione di pomata', placeholder: 'es. 1 applicazione' };
    if (f.includes('spray cutaneo')) return { via: 'topica', unitLabel: 'erogazione spray', placeholder: 'es. 1 erogazione' };
    // Cerotti
    if (f.includes('cerotto') || f.includes('patch')) return { via: 'transdermica', unitLabel: 'cerotto', placeholder: 'es. 1 cerotto' };
    // Collirio
    if (f.includes('collirio') || f.includes('oftalmico')) return { via: 'oftalmica', unitLabel: 'goccia oculare', placeholder: 'es. 1 goccia per occhio' };
    // Gocce nasali
    if (f.includes('nasale') || f.includes('spray nasale')) return { via: 'altro', unitLabel: 'erogazione nasale', placeholder: 'es. 2 spray per narice' };
    // Gocce auricolari
    if (f.includes('auricolar')) return { via: 'altro', unitLabel: 'goccia auricolare', placeholder: 'es. 3 gocce per orecchio' };
    return { via: 'orale', unitLabel: 'unita', placeholder: 'es. 1 compressa / 1 fiala / 5 ml' };
  };

  const handlePhotoScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setScanPreview(dataUrl);
      setScanning(true);
      try {
        const base64 = await compressImageForAI(dataUrl);
        const res = await api.post('/farmaci/riconosci-immagine', { image_base64: base64, ospite_id: ospiteId });
        if (res.data.parsed && res.data.farmaco) {
          const f = res.data.farmaco;
          toast.success(`Riconosciuto: ${f.nome_commerciale || f.principio_attivo || 'Farmaco'}`);
          setForm(prev => {
            const detected = f.forma_farmaceutica ? detectViaAndUnit([f.forma_farmaceutica]) : null;
            const liquidUnits = ['gocce', 'ml', 'ml di sciroppo', 'puff'];
            const isLiquid = detected && liquidUnits.some(u => detected.unitLabel.includes(u));
            const autoPos = detected && detected.unitLabel !== 'unita' && !isLiquid ? `1 ${detected.unitLabel}` : '';
            return {
              ...prev,
              farmaco: f.nome_commerciale ? `${f.nome_commerciale}${f.dosaggio ? ' ' + f.dosaggio : ''}` : prev.farmaco,
              dosaggio: autoPos,
              principio_attivo: f.principio_attivo || prev.principio_attivo,
              confezione_unita: f.unita_confezione ? String(f.unita_confezione) : prev.confezione_unita,
              via: detected ? detected.via : prev.via,
              _unitLabel: detected ? detected.unitLabel : prev._unitLabel,
              _dosPlaceholder: detected ? detected.placeholder : prev._dosPlaceholder,
              _formaFarm: f.forma_farmaceutica || prev._formaFarm || '',
            };
          });
          if (f.nome_commerciale || f.principio_attivo) {
            checkAllergie(f.nome_commerciale || '', f.principio_attivo || '');
          }
        } else {
          toast.info('Farmaco non riconosciuto. Compila manualmente.');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (err.response?.status === 402 || detail.includes('Credito') || detail.includes('Budget')) {
          toast.error('Credito AI esaurito. Ricarica in Profilo > Universal Key.');
        } else {
          toast.error('Errore riconoscimento farmaco');
        }
      }
      setScanning(false);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSomministraOra = async () => {
    if (!sommDialog) return;
    const now = new Date();
    const orario = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const data = now.toISOString().slice(0, 10);
    try {
      await api.post('/somministrazioni', {
        terapia_id: sommDialog.id,
        ospite_id: ospiteId,
        data,
        orario,
        stato: 'somministrato',
        note: sommNota,
      });
      toast.success(`Somministrato alle ${orario}`);
      setSommDialog(null);
      setSommNota('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleSave = async () => {
    if (!form.farmaco) { toast.error('Compila il farmaco'); return; }
    if (!form.al_bisogno && form.orari.length === 0) { toast.error('Seleziona almeno un orario oppure imposta come terapia al bisogno'); return; }
    // Build dosaggi_per_orario and summary dosaggio
    let dosaggio = form.dosaggio;
    let dosaggiPerOrario = [];
    if (form.dosaggio_diverso && form.orari.length > 0) {
      dosaggiPerOrario = form.orari.map(o => ({
        orario: o,
        quantita: form.dosaggi_per_orario[o] || form.dosaggio || '1 compressa'
      }));
      const quantities = dosaggiPerOrario.map(d => d.quantita);
      if (new Set(quantities).size === 1) {
        dosaggio = quantities[0];
      } else {
        dosaggio = dosaggiPerOrario.map(d => `${d.quantita} (${d.orario})`).join(', ');
      }
    }
    if (!dosaggio) { toast.error('Compila il dosaggio'); return; }
    try {
      const payload = {
        ...form,
        dosaggio,
        dosaggi_per_orario: dosaggiPerOrario.length > 0 ? dosaggiPerOrario : form.orari.map(o => ({ orario: o, quantita: dosaggio })),
        ospite_id: ospiteId,
        orari: form.al_bisogno ? [] : form.orari,
        durata_fine: form.durata_fine === 'mai' ? '' : form.durata_fine,
        confezione_unita: parseInt(form.confezione_unita) || 0,
        scorta_attuale: parseInt(form.scorta_attuale) || 0,
        soglia_riordino: parseInt(form.soglia_riordino) || 7,
      };
      delete payload.dosaggio_diverso;
      await api.post('/terapie', payload);
      toast.success('Terapia aggiunta'); setShowForm(false); setAlertAllergie([]);
      setForm({ farmaco: '', dosaggio: '', via: 'orale', orari: [], al_bisogno: false, durata_inizio: new Date().toISOString().slice(0, 10), durata_fine: 'mai', note: '', principio_attivo: '', confezione_unita: '', scorta_attuale: '', soglia_riordino: '7', giorni_settimana: ['lun','mar','mer','gio','ven','sab','dom'], schema_giorni: 'tutti', dosaggi_per_orario: {}, dosaggio_diverso: false });
      fetchTerapie();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  // Sospensione dialog
  const [suspendDialog, setSuspendDialog] = useState(null);
  const [suspendNota, setSuspendNota] = useState('');

  const handleSuspend = async (id, nota) => {
    try {
      await api.put(`/terapie/${id}`, { stato: 'sospesa', nota_cambio: nota || undefined });
      toast.success('Terapia sospesa'); setSuspendDialog(null); setSuspendNota(''); fetchTerapie();
    } catch { toast.error('Errore'); }
  };

  const handleRifornimento = async () => {
    if (!rifQta || !rifornimentoId) return;
    try {
      await api.post(`/terapie/${rifornimentoId}/rifornimento?quantita=${parseInt(rifQta)}`);
      toast.success('Scorta aggiornata'); setRifornimentoId(null); setRifQta(''); fetchTerapie();
    } catch { toast.error('Errore aggiornamento scorta'); }
  };

  const getScortaInfo = (t) => {
    const isAB = t.al_bisogno || false;
    const somm = isAB ? 0 : (t.orari || []).length;
    const scorta = t.scorta_attuale || 0;
    const giorni = somm > 0 ? Math.floor(scorta / somm) : 999;
    const soglia = t.soglia_riordino || 7;
    return { scorta, giorni, inEsaurimento: !isAB && giorni <= soglia, somm, isAB };
  };

  return (
    <div className="space-y-4" data-testid="terapie-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-slate-700">Terapie</h3>
        {hasRole('medico', 'coordinatore') && (
          <Dialog open={showForm} onOpenChange={(v) => { setShowForm(v); if (!v) setAlertAllergie([]); }}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-terapia-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-1" />Nuova Terapia</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Nuova Prescrizione</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {alertAllergie.length > 0 && (
                  <div className="space-y-2" data-testid="allergia-alerts">
                    {alertAllergie.map((a, i) => (
                      <div key={i} className={`p-3 rounded-lg border-l-4 ${a.gravita === 'grave' ? 'bg-red-50 border-red-500 text-red-800' : a.tipo === 'doppia_prescrizione' ? 'bg-amber-50 border-amber-500 text-amber-800' : a.tipo === 'reattivita_crociata' ? 'bg-purple-50 border-purple-500 text-purple-800' : 'bg-orange-50 border-orange-400 text-orange-800'}`}>
                        <div className="flex items-center gap-2">
                          <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${a.gravita === 'grave' ? 'text-red-600' : 'text-amber-600'}`} />
                          <span className="text-xs font-bold uppercase">{
                            a.tipo === 'intolleranza_diretta' ? 'Intolleranza Diretta' :
                            a.tipo === 'intolleranza_categoria' ? 'Intolleranza di Categoria' :
                            a.tipo === 'intolleranza_stessa_classe' ? 'Stessa Classe Farmacologica' :
                            a.tipo === 'reattivita_crociata' ? 'Reattivita Crociata' :
                            a.tipo === 'doppia_prescrizione' ? 'Doppia Prescrizione' :
                            `Allergia ${a.gravita}`
                          }</span>
                        </div>
                        <p className="text-xs mt-1">{a.messaggio}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <Label className="text-xs">Farmaco * <span className="text-[10px] text-blue-500 font-normal">(Database AIFA)</span></Label>
                  <FarmacoAutocomplete data-testid="terapia-farmaco" value={form.farmaco}
                    onChange={v => { setForm(f => ({ ...f, farmaco: v })); }}
                    onSelectFull={(item, dos) => {
                      // Try to match dosage to the most appropriate form
                      let formeToUse = item.forme;
                      if (item.forme && item.forme.length > 1 && dos) {
                        const dosLow = dos.toLowerCase();
                        // Liquid dosages (e.g., "120 MG/5 ML") → prefer liquid forms
                        if (dosLow.includes('/ml') || dosLow.includes('/ ml') || dosLow.includes('/5 ml') || dosLow.includes('ml')) {
                          const liquidForm = item.forme.find(f => {
                            const fl = f.toLowerCase();
                            return fl.includes('sciroppo') || fl.includes('sospensione') || fl.includes('soluzione orale') || fl.includes('gocce');
                          });
                          if (liquidForm) formeToUse = [liquidForm];
                        }
                      }
                      const detected = detectViaAndUnit(formeToUse);
                      handleDrugSelect(`${item.nome} ${dos}`, item.principio_attivo);
                      // Auto-fill: "1 X" per unità contabili, placeholder per liquidi
                      const liquidUnits = ['gocce', 'ml', 'ml di sciroppo', 'puff'];
                      const isLiquid = liquidUnits.some(u => detected.unitLabel.includes(u));
                      const autoPos = detected.unitLabel !== 'unita' && !isLiquid ? `1 ${detected.unitLabel}` : '';
                      setForm(f => ({ ...f, dosaggio: autoPos, farmaco: `${item.nome} ${dos}`, principio_attivo: item.principio_attivo || '', via: detected.via, _unitLabel: detected.unitLabel, _dosPlaceholder: detected.placeholder, _formaFarm: (formeToUse || [])[0] || '' }));
                    }}
                    onSelectDosaggio={d => setForm(f => ({ ...f }))}
                  />
                  <div className="flex items-center gap-2 mt-1.5">
                    <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoScan} data-testid="foto-farmaco-terapia" />
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs text-brand border-blue-200 hover:bg-blue-50" onClick={() => fileInputRef.current?.click()} disabled={scanning} data-testid="foto-farmaco-terapia-btn">
                      {scanning ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Riconoscimento...</> : <><Camera className="w-3.5 h-3.5 mr-1" />Fotografa Scatola</>}
                    </Button>
                    {scanPreview && !scanning && <img src={scanPreview} alt="preview" className="h-7 w-7 rounded border object-cover" />}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Posologia (quantita) *</Label>
                    <Input data-testid="terapia-dosaggio" value={form.dosaggio} onChange={e => setForm(f => ({ ...f, dosaggio: e.target.value }))} placeholder={form._dosPlaceholder || 'es. 1 compressa / 1 fiala'} />
                    {form._formaFarm && <p className="text-[10px] text-blue-500 mt-0.5">Forma: {form._formaFarm}</p>}
                    {/* Quick dose buttons */}
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {DOSI_RAPIDE.map(qty => {
                        const unit = form._unitLabel || 'compressa';
                        const pluralUnit = parseFloat(qty) > 1 ? unit.replace(/^compressa/, 'compresse').replace(/^capsula/, 'capsule') : unit;
                        return (
                          <button key={qty} type="button"
                            onClick={() => setForm(f => ({ ...f, dosaggio: `${qty} ${pluralUnit}` }))}
                            className="px-1.5 py-0.5 text-[10px] rounded border border-slate-200 hover:border-brand hover:bg-blue-50 text-slate-600 transition-all"
                            data-testid={`quick-dose-${qty}`}>{qty} {pluralUnit.split(' ')[0]}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div><Label className="text-xs">Via {form._formaFarm && <span className="text-[10px] text-emerald-500 font-normal">(auto)</span>}</Label>
                    <Select value={form.via} onValueChange={v => setForm(f => ({ ...f, via: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="orale">Orale</SelectItem><SelectItem value="sublinguale">Sublinguale</SelectItem><SelectItem value="endovenosa">Endovenosa</SelectItem><SelectItem value="intramuscolare">Intramuscolare</SelectItem><SelectItem value="sottocutanea">Sottocutanea</SelectItem><SelectItem value="transdermica">Transdermica</SelectItem><SelectItem value="rettale">Rettale</SelectItem><SelectItem value="inalatoria">Inalatoria</SelectItem><SelectItem value="topica">Topica</SelectItem><SelectItem value="oftalmica">Oftalmica</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div><Label className="text-xs">Principio Attivo</Label><Input value={form.principio_attivo} onChange={e => setForm(f => ({ ...f, principio_attivo: e.target.value }))} placeholder="Auto-compilato da AIFA" className="text-xs" /></div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/50">
                  <input type="checkbox" id="al_bisogno" checked={form.al_bisogno} onChange={e => setForm(f => ({ ...f, al_bisogno: e.target.checked, orari: e.target.checked ? [] : f.orari }))} className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500" data-testid="al-bisogno-checkbox" />
                  <div>
                    <Label htmlFor="al_bisogno" className="text-xs font-semibold text-amber-800 cursor-pointer">Terapia Al Bisogno (AB)</Label>
                    <p className="text-[10px] text-amber-600 mt-0.5">Somministrazione solo in caso di necessita, senza orari fissi</p>
                  </div>
                </div>
                {!form.al_bisogno && <>
                <div>
                  <Label className="text-xs">Orari Somministrazione *</Label>
                  <div className="flex gap-1.5 flex-wrap mt-1">
                    {ORARI_PRESET.map(o => (
                      <button key={o} type="button" onClick={() => toggleOrario(o)}
                        className={`px-2.5 py-1 text-xs rounded-md border transition-all font-mono ${form.orari.includes(o) ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                        data-testid={`orario-${o}`}>{o}</button>
                    ))}
                  </div>
                </div>
                {form.orari.length > 1 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="dosaggio_diverso" checked={form.dosaggio_diverso}
                        onChange={e => setForm(f => ({ ...f, dosaggio_diverso: e.target.checked }))}
                        className="w-3.5 h-3.5 rounded border-blue-400 text-brand focus:ring-blue-500" data-testid="dosaggio-diverso-toggle" />
                      <Label htmlFor="dosaggio_diverso" className="text-xs cursor-pointer text-blue-700">Dosaggio diverso per ogni orario</Label>
                    </div>
                    {form.dosaggio_diverso && (
                      <div className="grid grid-cols-2 gap-2 p-2 rounded-lg bg-blue-50/50 border border-blue-100">
                        {form.orari.sort().map(o => (
                          <div key={o} className="space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-mono font-bold text-blue-700 w-10">{o}</span>
                              <Input
                                value={form.dosaggi_per_orario[o] || ''}
                                onChange={e => setForm(f => ({ ...f, dosaggi_per_orario: { ...f.dosaggi_per_orario, [o]: e.target.value } }))}
                                placeholder={form.dosaggio || '1 compressa (o valore libero: 10 gocce)'}
                                className="h-7 text-xs"
                                data-testid={`dosaggio-orario-${o}`}
                              />
                            </div>
                            <div className="flex gap-1 flex-wrap pl-11">
                              {DOSI_RAPIDE.map(qty => (
                                <button key={qty} type="button"
                                  onClick={() => setForm(f => ({ ...f, dosaggi_per_orario: { ...f.dosaggi_per_orario, [o]: `${qty} ${form._unitLabel || 'compressa'}` } }))}
                                  className="px-1.5 py-0.5 text-[10px] rounded border border-slate-200 hover:border-brand hover:bg-blue-50 text-slate-600 transition-all"
                                  data-testid={`dose-orario-${o}-${qty}`}>{qty}</button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-xs">Giorni della Settimana</Label>
                  <div className="flex gap-1 mt-1">
                    <button type="button" onClick={() => setSchemaGiorni('tutti')} className={`px-2 py-1 text-[10px] rounded border ${form.schema_giorni === 'tutti' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200'}`}>Tutti</button>
                    <button type="button" onClick={() => setSchemaGiorni('alterni_pari')} className={`px-2 py-1 text-[10px] rounded border ${form.schema_giorni === 'alterni_pari' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200'}`}>Alterni (L-M-V-D)</button>
                    <button type="button" onClick={() => setSchemaGiorni('alterni_dispari')} className={`px-2 py-1 text-[10px] rounded border ${form.schema_giorni === 'alterni_dispari' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200'}`}>Alterni (M-G-S)</button>
                    <button type="button" onClick={() => setSchemaGiorni('personalizzato')} className={`px-2 py-1 text-[10px] rounded border ${form.schema_giorni === 'personalizzato' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200'}`}>Personalizzato</button>
                  </div>
                  <div className="flex gap-1 mt-1.5">
                    {GIORNI.map(g => (
                      <button key={g.key} type="button" onClick={() => { toggleGiorno(g.key); setForm(f => ({ ...f, schema_giorni: 'personalizzato' })); }}
                        className={`px-2 py-1 text-[10px] rounded-md border transition-all ${form.giorni_settimana.includes(g.key) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-500 border-slate-200'}`}
                        data-testid={`giorno-${g.key}`}>{g.label}</button>
                    ))}
                  </div>
                </div>
                </>}
                <div><Label className="text-xs">Data Inizio</Label><div className="mt-1.5"><DatePickerField value={form.durata_inizio} onChange={v => setForm(f => ({ ...f, durata_inizio: v }))} testId="durata-inizio-input" /></div></div>
                <div>
                  <Label className="text-xs">Data Fine Terapia</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <button type="button" onClick={() => setForm(f => ({ ...f, durata_fine: 'mai' }))}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-all font-medium ${form.durata_fine === 'mai' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}
                      data-testid="fine-mai">Mai (a vita)</button>
                    <button type="button" onClick={() => setForm(f => ({ ...f, durata_fine: f.durata_fine === 'mai' ? new Date(Date.now() + 7*86400000).toISOString().slice(0, 10) : f.durata_fine }))}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-all font-medium ${form.durata_fine !== 'mai' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}
                      data-testid="fine-data">Data specifica</button>
                  </div>
                  {form.durata_fine !== 'mai' && (
                    <div className="mt-1.5"><DatePickerField value={form.durata_fine} onChange={v => setForm(f => ({ ...f, durata_fine: v }))} testId="durata-fine-input" /></div>
                  )}
                </div>
                <Separator />
                <p className="text-xs font-semibold text-slate-500">Gestione Scorte</p>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs">Unita/Confezione</Label><Input type="number" value={form.confezione_unita} onChange={e => setForm(f => ({ ...f, confezione_unita: e.target.value }))} placeholder="es. 30" /></div>
                  <div><Label className="text-xs">Scorta Attuale</Label><Input type="number" value={form.scorta_attuale} onChange={e => setForm(f => ({ ...f, scorta_attuale: e.target.value }))} placeholder="es. 60" /></div>
                  <div><Label className="text-xs">Soglia Riordino (gg)</Label><Input type="number" value={form.soglia_riordino} onChange={e => setForm(f => ({ ...f, soglia_riordino: e.target.value }))} /></div>
                </div>
                <div><Label className="text-xs">Note</Label><DictableTextarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} /></div>
                <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => { setShowForm(false); setAlertAllergie([]); }}>Annulla</Button><Button data-testid="save-terapia" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">Salva</Button></div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {/* Rifornimento dialog */}
      <Dialog open={!!rifornimentoId} onOpenChange={(v) => { if (!v) { setRifornimentoId(null); setRifQta(''); } }}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Registra Rifornimento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Quantita ricevuta</Label><Input type="number" value={rifQta} onChange={e => setRifQta(e.target.value)} placeholder="es. 30" data-testid="rifornimento-qta" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setRifornimentoId(null)}>Annulla</Button><Button onClick={handleRifornimento} data-testid="save-rifornimento" className="bg-emerald-600 hover:bg-emerald-700 text-white">Conferma</Button></div>
          </div>
        </DialogContent>
      </Dialog>
      {terapie.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Nessuna terapia</p> : (
        <Table>
          <TableHeader><TableRow><TableHead>Farmaco</TableHead><TableHead>Dosaggio</TableHead><TableHead>Via</TableHead><TableHead>Orari</TableHead><TableHead>Fine</TableHead><TableHead>Scorta</TableHead><TableHead>Stato</TableHead>{hasRole('medico', 'coordinatore', 'infermiere') && <TableHead></TableHead>}</TableRow></TableHeader>
          <TableBody>
            {terapie.map(t => {
              const s = getScortaInfo(t);
              const fineLabel = t.durata_fine ? new Date(t.durata_fine).toLocaleDateString('it-IT') : 'Mai';
              const isScaduta = t.durata_fine && new Date(t.durata_fine) < new Date();
              return (
              <TableRow key={t.id} className={s.inEsaurimento && t.stato === 'attiva' ? 'bg-red-50/50' : ''}>
                <TableCell className="font-medium text-sm">
                  {t.farmaco}
                  {t.principio_attivo && <span className="block text-[10px] text-slate-400">{t.principio_attivo}</span>}
                </TableCell>
                <TableCell className="text-sm">
                  {t.dosaggi_per_orario && t.dosaggi_per_orario.length > 1 && new Set(t.dosaggi_per_orario.map(d => d.quantita)).size > 1
                    ? <div className="space-y-0.5">
                        {t.dosaggi_per_orario.map((d, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <span className="text-[10px] font-mono text-brand bg-blue-50 px-1 rounded">{d.orario}</span>
                            <span className="text-xs">{d.quantita}</span>
                          </div>
                        ))}
                      </div>
                    : t.dosaggio
                  }
                </TableCell>
                <TableCell className="text-sm capitalize">{t.via}</TableCell>
                <TableCell className="text-sm">{t.al_bisogno ? <Badge className="bg-amber-100 text-amber-700 text-[10px]">Al Bisogno</Badge> : t.orari?.join(', ')}</TableCell>
                <TableCell>
                  <span className={`text-xs ${isScaduta ? 'text-red-600 font-bold' : fineLabel === 'Mai' ? 'text-slate-400' : 'text-slate-600'}`}>
                    {fineLabel}
                    {isScaduta && <span className="block text-[10px]">Scaduta</span>}
                  </span>
                </TableCell>
                <TableCell>
                  {t.stato === 'attiva' ? (
                    <div className="text-xs">
                      <span className={`font-bold ${s.inEsaurimento ? 'text-red-600' : 'text-emerald-600'}`}>{s.scorta} unita</span>
                      {s.somm > 0 && <span className={`block text-[10px] ${s.inEsaurimento ? 'text-red-500' : 'text-slate-400'}`}>{s.giorni} gg rimasti</span>}
                    </div>
                  ) : <span className="text-xs text-slate-400">-</span>}
                </TableCell>
                <TableCell><Badge className={t.stato === 'attiva' ? (s.inEsaurimento ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700') : 'bg-slate-100 text-slate-600'} variant="outline">{t.stato}{s.inEsaurimento && t.stato === 'attiva' ? ' !' : ''}</Badge></TableCell>
                {hasRole('medico', 'coordinatore', 'infermiere') && (
                  <TableCell>
                    <div className="flex gap-1">
                      {t.stato === 'attiva' && hasRole('infermiere', 'coordinatore', 'medico') && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600 hover:text-amber-700" onClick={() => { setSommDialog(t); setSommNota(''); }} data-testid={`somministra-ora-btn-${t.id}`}>
                          <Zap className="w-3.5 h-3.5 mr-0.5" />Somministra
                        </Button>
                      )}
                      {t.stato === 'attiva' && <Button variant="ghost" size="sm" className="text-xs text-emerald-600 hover:text-emerald-700" onClick={() => setRifornimentoId(t.id)} data-testid={`rifornimento-btn-${t.id}`}>+Scorta</Button>}
                      {t.stato === 'attiva' && hasRole('medico', 'coordinatore') && <Button variant="ghost" size="sm" className="text-xs text-brand hover:text-brand-dark" onClick={() => openEditDialog(t)} data-testid={`modifica-terapia-btn-${t.id}`}><Pencil className="w-3.5 h-3.5 mr-0.5" />Modifica</Button>}
                      {t.stato === 'attiva' && hasRole('medico', 'coordinatore') && <Button variant="ghost" size="sm" className="text-xs text-red-600 hover:text-red-700" onClick={() => { setSuspendDialog(t); setSuspendNota(''); }}>Sospendi</Button>}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );})}
          </TableBody>
        </Table>
      )}
      {/* Dialog Somministra Ora */}
      <Dialog open={!!sommDialog} onOpenChange={(v) => { if (!v) { setSommDialog(null); setSommNota(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-600" />Somministra Ora</DialogTitle></DialogHeader>
          {sommDialog && (
            <div className="space-y-3">
              <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm font-bold text-slate-800">{sommDialog.farmaco}</p>
                <p className="text-xs text-slate-500">{sommDialog.dosaggio} - {sommDialog.via}</p>
                {sommDialog.al_bisogno && <Badge className="bg-amber-100 text-amber-700 text-[10px] mt-1">Al Bisogno</Badge>}
              </div>
              <p className="text-xs text-slate-500">Orario: <span className="font-mono font-bold text-slate-800">{new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span></p>
              <div><Label className="text-xs">Note (opzionale)</Label><DictableTextarea value={sommNota} onChange={e => setSommNota(e.target.value)} rows={2} placeholder="es. Cefalea, dolore, febbre..." data-testid="somministra-ora-nota" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setSommDialog(null); setSommNota(''); }}>Annulla</Button>
                <Button onClick={handleSomministraOra} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="conferma-somministra-ora">Conferma</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Sospensione con Nota */}
      <Dialog open={!!suspendDialog} onOpenChange={(v) => { if (!v) { setSuspendDialog(null); setSuspendNota(''); } }}>
        <DialogContent className="max-w-sm" data-testid="suspend-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" />Sospendi Terapia</DialogTitle></DialogHeader>
          {suspendDialog && (
            <div className="space-y-3">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-semibold text-red-800">{suspendDialog.farmaco}</p>
                <p className="text-xs text-red-600">{suspendDialog.dosaggio} - {suspendDialog.via}</p>
              </div>
              <div>
                <Label className="text-xs">Motivo della sospensione <span className="text-slate-400">(facoltativo)</span></Label>
                <Input value={suspendNota} onChange={e => setSuspendNota(e.target.value)} placeholder="Es. effetti collaterali, sostituzione farmaco, fine ciclo..." data-testid="suspend-nota-input" autoFocus />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setSuspendDialog(null); setSuspendNota(''); }}>Annulla</Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleSuspend(suspendDialog.id, suspendNota)} data-testid="confirm-suspend-btn">Sospendi Terapia</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === DIALOG MODIFICA TERAPIA === */}
      <Dialog open={!!editDialog} onOpenChange={(v) => { if (!v) { setEditDialog(null); setEditForm(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-terapia-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-brand" />Modifica Terapia</DialogTitle></DialogHeader>
          {editDialog && editForm && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-sm font-bold text-blue-800">{editDialog.farmaco}</p>
                {editDialog.principio_attivo && <p className="text-xs text-brand">{editDialog.principio_attivo}</p>}
              </div>

              {/* Dosaggio */}
              <div>
                <Label className="text-xs">Posologia</Label>
                <Input value={editForm.dosaggio} onChange={e => setEditForm(f => ({ ...f, dosaggio: e.target.value }))} data-testid="edit-dosaggio" />
                <div className="flex gap-1 mt-1 flex-wrap">
                  {DOSI_RAPIDE.map(qty => {
                    const unit = editDialog.forma_farmaceutica_aifa ? (editDialog.forma_farmaceutica_aifa.toLowerCase().includes('capsula') ? 'capsula' : 'compressa') : 'compressa';
                    const plural = parseFloat(qty) > 1 ? unit.replace('compressa', 'compresse').replace('capsula', 'capsule') : unit;
                    return (
                      <button key={qty} type="button" onClick={() => setEditForm(f => ({ ...f, dosaggio: `${qty} ${plural}` }))}
                        className="px-1.5 py-0.5 text-[10px] rounded border border-slate-200 hover:border-brand hover:bg-blue-50 text-slate-600 transition-all">{qty} {plural.split(' ')[0]}</button>
                    );
                  })}
                </div>
              </div>

              {/* Via */}
              <div>
                <Label className="text-xs">Via di Somministrazione</Label>
                <Select value={editForm.via} onValueChange={v => setEditForm(f => ({ ...f, via: v }))}>
                  <SelectTrigger data-testid="edit-via"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Orale','Sottocutanea','Intramuscolare','Endovenosa','Transdermica','Inalatoria','Rettale','Oftalmica','Topica'].map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Al bisogno toggle */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="edit_ab" checked={editForm.al_bisogno} onChange={e => setEditForm(f => ({ ...f, al_bisogno: e.target.checked }))} className="w-3.5 h-3.5" data-testid="edit-al-bisogno" />
                <Label htmlFor="edit_ab" className="text-xs cursor-pointer">Terapia al bisogno</Label>
              </div>

              {/* Orari */}
              {!editForm.al_bisogno && (
                <>
                  <div>
                    <Label className="text-xs">Orari Somministrazione</Label>
                    <div className="flex gap-1.5 flex-wrap mt-1">
                      {ORARI_PRESET.map(o => (
                        <button key={o} type="button" onClick={() => toggleEditOrario(o)}
                          className={`px-2.5 py-1 text-xs rounded-md border transition-all font-mono ${editForm.orari.includes(o) ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                          data-testid={`edit-orario-${o}`}>{o}</button>
                      ))}
                    </div>
                  </div>

                  {/* Dosaggio diverso per orario */}
                  {editForm.orari.length > 1 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="edit_dd" checked={editForm.dosaggio_diverso}
                          onChange={e => setEditForm(f => ({ ...f, dosaggio_diverso: e.target.checked }))} className="w-3.5 h-3.5" data-testid="edit-dosaggio-diverso" />
                        <Label htmlFor="edit_dd" className="text-xs cursor-pointer text-blue-700">Dosaggio diverso per ogni orario</Label>
                      </div>
                      {editForm.dosaggio_diverso && (
                        <div className="grid grid-cols-2 gap-2 p-2 rounded-lg bg-blue-50/50 border border-blue-100">
                          {editForm.orari.sort().map(o => (
                            <div key={o} className="space-y-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-mono font-bold text-blue-700 w-10">{o}</span>
                                <Input value={editForm.dosaggi_per_orario[o] || ''} onChange={e => setEditForm(f => ({ ...f, dosaggi_per_orario: { ...f.dosaggi_per_orario, [o]: e.target.value } }))}
                                  placeholder={editForm.dosaggio || '1 compressa (o valore libero: 10 gocce)'} className="h-7 text-xs" data-testid={`edit-dpo-${o}`} />
                              </div>
                              <div className="flex gap-1 flex-wrap pl-11">
                                {DOSI_RAPIDE.map(qty => (
                                  <button key={qty} type="button"
                                    onClick={() => setEditForm(f => ({ ...f, dosaggi_per_orario: { ...f.dosaggi_per_orario, [o]: `${qty} compressa` } }))}
                                    className="px-1.5 py-0.5 text-[10px] rounded border border-slate-200 hover:border-brand hover:bg-blue-50 text-slate-600 transition-all"
                                    data-testid={`edit-dose-orario-${o}-${qty}`}>{qty}</button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Giorni */}
                  <div>
                    <Label className="text-xs">Giorni</Label>
                    <div className="flex gap-1 mt-1">
                      <button type="button" onClick={() => setEditSchema('tutti')} className={`px-2 py-1 text-[10px] rounded border ${editForm.schema_giorni === 'tutti' ? 'bg-brand text-white' : 'bg-white text-slate-500 border-slate-200'}`}>Tutti</button>
                      <button type="button" onClick={() => setEditSchema('alterni_pari')} className={`px-2 py-1 text-[10px] rounded border ${editForm.schema_giorni === 'alterni_pari' ? 'bg-brand text-white' : 'bg-white text-slate-500 border-slate-200'}`}>L-M-V-D</button>
                      <button type="button" onClick={() => setEditSchema('alterni_dispari')} className={`px-2 py-1 text-[10px] rounded border ${editForm.schema_giorni === 'alterni_dispari' ? 'bg-brand text-white' : 'bg-white text-slate-500 border-slate-200'}`}>M-G-S</button>
                      <button type="button" onClick={() => setEditSchema('personalizzato')} className={`px-2 py-1 text-[10px] rounded border ${editForm.schema_giorni === 'personalizzato' ? 'bg-brand text-white' : 'bg-white text-slate-500 border-slate-200'}`}>Custom</button>
                    </div>
                    <div className="flex gap-1 mt-1.5">
                      {GIORNI.map(g => (
                        <button key={g.key} type="button" onClick={() => { toggleEditGiorno(g.key); setEditForm(f => ({ ...f, schema_giorni: 'personalizzato' })); }}
                          className={`px-2 py-1 text-[10px] rounded-md border ${editForm.giorni_settimana.includes(g.key) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-500 border-slate-200'}`}>{g.label}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Data Fine */}
              <div>
                <Label className="text-xs">Data Fine</Label>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => setEditForm(f => ({ ...f, durata_fine: 'mai' }))}
                    className={`px-3 py-1.5 text-xs rounded-md border font-medium ${editForm.durata_fine === 'mai' ? 'bg-brand text-white' : 'bg-white text-slate-500 border-slate-200'}`}>Mai (a vita)</button>
                  <button type="button" onClick={() => setEditForm(f => ({ ...f, durata_fine: f.durata_fine === 'mai' ? new Date(Date.now() + 7*86400000).toISOString().slice(0, 10) : f.durata_fine }))}
                    className={`px-3 py-1.5 text-xs rounded-md border font-medium ${editForm.durata_fine !== 'mai' ? 'bg-brand text-white' : 'bg-white text-slate-500 border-slate-200'}`}>Data specifica</button>
                </div>
                {editForm.durata_fine !== 'mai' && (
                  <div className="mt-1.5"><DatePickerField value={editForm.durata_fine} onChange={v => setEditForm(f => ({ ...f, durata_fine: v }))} testId="edit-durata-fine" /></div>
                )}
              </div>

              {/* Note */}
              <div>
                <Label className="text-xs">Note</Label>
                <DictableTextarea value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} rows={2} data-testid="edit-note" />
              </div>

              {/* Nota cambio (motivo modifica) */}
              <div>
                <Label className="text-xs">Motivo della modifica <span className="text-slate-400">(facoltativo)</span></Label>
                <Input value={editForm.nota_cambio} onChange={e => setEditForm(f => ({ ...f, nota_cambio: e.target.value }))} placeholder="Es. aggiustamento dosaggio, cambio orario..." data-testid="edit-nota-cambio" />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => { setEditDialog(null); setEditForm(null); }}>Annulla</Button>
                <Button onClick={handleEditSave} className="bg-brand hover:bg-brand-dark text-white" data-testid="save-edit-terapia">Salva Modifiche</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === STORICO TERAPIE === */}
      <div className="mt-6 border-t pt-4">
        <button onClick={() => setShowStorico(!showStorico)} className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-800 transition-colors" data-testid="toggle-storico-terapie">
          <Clock className="w-4 h-4" />Storico Modifiche Terapie
          <Badge className="bg-slate-100 text-slate-600 text-[10px]">{storico.length}</Badge>
          <span className="text-[10px] text-slate-400 ml-1">{showStorico ? '(nascondi)' : '(mostra)'}</span>
        </button>
        {showStorico && (
          <div className="mt-3 space-y-2 max-h-[400px] overflow-y-auto" data-testid="storico-terapie-list">
            {storico.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">Nessun evento registrato</p>
            ) : (
              storico.map(ev => {
                const evIcons = { inserimento: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Inserimento' }, sospensione: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Sospensione' }, riattivazione: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Riattivazione' }, modifica: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Modifica' } };
                const evStyle = evIcons[ev.evento] || evIcons.modifica;
                return (
                  <div key={ev.id} className={`flex items-start gap-3 p-2.5 rounded-lg border ${evStyle.color}`} data-testid={`storico-item-${ev.id}`}>
                    <div className="flex-shrink-0 mt-0.5">
                      <Badge className={`text-[9px] ${evStyle.color}`}>{evStyle.label}</Badge>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{ev.farmaco}</p>
                      <p className="text-[10px] mt-0.5">{ev.dettagli}</p>
                      <p className="text-[10px] opacity-60 mt-1">{ev.operatore_nome} ({ev.ruolo}) — {new Date(ev.timestamp).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// === PARAMETRI VITALI TAB ===
function ParametriTab({ ospiteId, ospite }) {
  const { hasRole } = useAuth();
  const [parametri, setParametri] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedChart, setSelectedChart] = useState('PA');
  const [filtroTipo, setFiltroTipo] = useState('tutti');
  const [vistaMode, setVistaMode] = useState('trend'); // trend, giornaliero, mensile
  const [form, setForm] = useState({ tipo: 'PA', sistolica: '', diastolica: '', valore: '', valore_testo: '', data_rilevazione: new Date().toISOString().slice(0, 16), note: '' });
  const tipiParam = { PA: 'Pressione Arteriosa', FC: 'Freq. Cardiaca', FR: 'Freq. Respiratoria', SpO2: 'SpO2', temperatura: 'Temperatura', glicemia: 'Glicemia', peso: 'Peso', alvo: 'Alvo', diuresi: 'Diuresi', coscienza: 'Stato di coscienza (AVPU)', ossigeno: 'Ossigeno supplementare' };
  const tipiParamFull = { PA: 'Pressione Arteriosa (mmHg)', FC: 'Freq. Cardiaca (bpm)', FR: 'Freq. Respiratoria (atti/min)', SpO2: 'SpO2 (%)', temperatura: 'Temperatura (C)', glicemia: 'Glicemia (mg/dL)', peso: 'Peso (kg)', alvo: 'Alvo', diuresi: 'Diuresi', coscienza: 'Stato di coscienza (AVPU)', ossigeno: 'Ossigeno supplementare' };
  const unitaMap = { PA: 'mmHg', FC: 'bpm', FR: 'atti/min', SpO2: '%', temperatura: 'C', glicemia: 'mg/dL', peso: 'kg', alvo: '', diuresi: '' };
  const chartColors = { PA: '#2563eb', FC: '#ef4444', FR: '#f59e0b', SpO2: '#10b981', temperatura: '#8b5cf6', glicemia: '#ec4899', peso: '#6366f1', alvo: '#84cc16', diuresi: '#06b6d4', coscienza: '#0f766e', ossigeno: '#0284c7' };

  const ALVO_OPTIONS = ['Regolare', 'Stipsi', 'Diarrea', 'Assente'];
  const DIURESI_OPTIONS = ['Regolare', 'Ridotta', 'Abbondante', 'Assente/Anuria', 'Cateterizzato'];
  const COSCIENZA_OPTIONS = ['A - Vigile', 'C - Confuso', 'V - Risponde alla voce', 'P - Risponde al dolore', 'U - Non risponde'];
  const OSSIGENO_OPTIONS = ['Aria ambiente', 'O2 occhialini', 'O2 maschera', 'O2 Venturi', 'O2 alti flussi'];
  const TIPI_TESTO = ['alvo', 'diuresi', 'coscienza', 'ossigeno'];

  const fetchParametri = useCallback(() => {
    api.get('/parametri', { params: { ospite_id: ospiteId } }).then(r => setParametri(r.data)).catch(() => {});
  }, [ospiteId]);
  useEffect(() => { fetchParametri(); }, [fetchParametri]);

  const handleSave = async () => {
    if (form.tipo === 'PA') {
      if (!form.sistolica || !form.diastolica) { toast.error('Inserisci sistolica e diastolica'); return; }
      try {
        await api.post('/parametri', { tipo: 'PA_sistolica', ospite_id: ospiteId, valore: parseFloat(form.sistolica), unita: 'mmHg', data_rilevazione: form.data_rilevazione, note: form.note });
        await api.post('/parametri', { tipo: 'PA_diastolica', ospite_id: ospiteId, valore: parseFloat(form.diastolica), unita: 'mmHg', data_rilevazione: form.data_rilevazione, note: form.note });
        toast.success('PA registrata'); setShowForm(false); setForm({ tipo: 'PA', sistolica: '', diastolica: '', valore: '', valore_testo: '', data_rilevazione: new Date().toISOString().slice(0, 16), note: '' }); fetchParametri();
      } catch { toast.error('Errore'); }
    } else if (TIPI_TESTO.includes(form.tipo)) {
      if (!form.valore_testo) { toast.error('Seleziona un valore'); return; }
      try {
        await api.post('/parametri', { tipo: form.tipo, ospite_id: ospiteId, valore: 0, valore_testo: form.valore_testo, unita: '', data_rilevazione: form.data_rilevazione, note: form.note });
        toast.success('Parametro registrato'); setShowForm(false); setForm({ tipo: 'PA', sistolica: '', diastolica: '', valore: '', valore_testo: '', data_rilevazione: new Date().toISOString().slice(0, 16), note: '' }); fetchParametri();
      } catch { toast.error('Errore'); }
    } else {
      if (!form.valore) { toast.error('Inserisci il valore'); return; }
      try {
        await api.post('/parametri', { tipo: form.tipo, ospite_id: ospiteId, valore: parseFloat(form.valore), unita: unitaMap[form.tipo] || '', data_rilevazione: form.data_rilevazione, note: form.note });
        toast.success('Parametro registrato'); setShowForm(false); setForm({ tipo: 'PA', sistolica: '', diastolica: '', valore: '', valore_testo: '', data_rilevazione: new Date().toISOString().slice(0, 16), note: '' }); fetchParametri();
      } catch { toast.error('Errore'); }
    }
  };

  // Normalize tipo for filtering
  const normTipo = (t) => t === 'PA_sistolica' || t === 'PA_diastolica' ? 'PA' : t;
  const paramTypes = [...new Set(parametri.map(p => normTipo(p.tipo)))];

  // Filtered parametri
  const filteredParametri = filtroTipo === 'tutti' ? parametri : parametri.filter(p => normTipo(p.tipo) === filtroTipo);

  // Chart data for trend view
  const getChartData = (type) => {
    if (type === 'PA') {
      const sis = parametri.filter(p => p.tipo === 'PA_sistolica').reverse().slice(-20);
      const dia = parametri.filter(p => p.tipo === 'PA_diastolica').reverse().slice(-20);
      return sis.map((s, i) => ({
        data: new Date(s.data_rilevazione).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
        sistolica: s.valore, diastolica: dia[i]?.valore || 0,
      }));
    }
    return parametri.filter(p => p.tipo === type).reverse().slice(-20).map(p => ({
      data: new Date(p.data_rilevazione).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
      valore: p.valore
    }));
  };

  // Helper: classify time to turno
  const getTurno = (dateStr) => {
    const h = new Date(dateStr).getHours();
    if (h >= 6 && h < 14) return 'mattina';
    if (h >= 14 && h < 22) return 'pomeriggio';
    return 'notte';
  };
  const turnoLabel = { mattina: 'Mattina (06-14)', pomeriggio: 'Pomeriggio (14-22)', notte: 'Notte (22-06)' };
  const turnoColors = { mattina: '#f59e0b', pomeriggio: '#2563eb', notte: '#6366f1' };

  // Data for giornaliero view: group by date+turno
  const getGiornalieroData = (type) => {
    const items = type === 'PA' ? parametri.filter(p => p.tipo === 'PA_sistolica') : parametri.filter(p => p.tipo === type);
    const byDate = {};
    items.forEach(p => {
      const d = p.data_rilevazione?.slice(0, 10) || '';
      const turno = getTurno(p.data_rilevazione);
      if (!byDate[d]) byDate[d] = {};
      if (type === 'PA') {
        byDate[d][`sis_${turno}`] = p.valore;
        const dia = parametri.find(pp => pp.tipo === 'PA_diastolica' && pp.data_rilevazione?.slice(0, 10) === d && getTurno(pp.data_rilevazione) === turno);
        if (dia) byDate[d][`dia_${turno}`] = dia.valore;
      } else {
        byDate[d][turno] = p.valore;
      }
    });
    return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)).slice(-14).map(([d, vals]) => ({
      data: new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      ...vals
    }));
  };

  // Data for mensile view: group by month, calculate averages
  const getMensileData = (type) => {
    const items = type === 'PA' ? parametri.filter(p => p.tipo === 'PA_sistolica') : parametri.filter(p => p.tipo === type);
    const byMonth = {};
    items.forEach(p => {
      const m = p.data_rilevazione?.slice(0, 7) || '';
      if (!byMonth[m]) byMonth[m] = { vals: [], count: 0 };
      byMonth[m].vals.push(p.valore);
      byMonth[m].count++;
    });
    if (type === 'PA') {
      const diaByMonth = {};
      parametri.filter(p => p.tipo === 'PA_diastolica').forEach(p => {
        const m = p.data_rilevazione?.slice(0, 7) || '';
        if (!diaByMonth[m]) diaByMonth[m] = [];
        diaByMonth[m].push(p.valore);
      });
      return Object.entries(byMonth).sort(([a], [b]) => a.localeCompare(b)).map(([m, { vals }]) => ({
        data: m, sistolica: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length),
        diastolica: Math.round((diaByMonth[m] || [0]).reduce((a, b) => a + b, 0) / (diaByMonth[m] || [1]).length),
        min_sis: Math.min(...vals), max_sis: Math.max(...vals),
      }));
    }
    return Object.entries(byMonth).sort(([a], [b]) => a.localeCompare(b)).map(([m, { vals }]) => ({
      data: m, valore: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10) / 10,
      min: Math.min(...vals), max: Math.max(...vals),
    }));
  };

  // Print clinical report
  const buildSvgChart = (dataPoints, color, label, unit, isPA = false) => {
    if (!dataPoints || dataPoints.length < 2) return '';
    const w = 500, h = 180, padL = 45, padR = 10, padT = 25, padB = 35;
    const chartW = w - padL - padR, chartH = h - padT - padB;
    let allVals, lines;
    if (isPA) {
      allVals = [...dataPoints.map(d => d.sistolica), ...dataPoints.map(d => d.diastolica)].filter(v => v > 0);
      const min = Math.min(...allVals) - 5, max = Math.max(...allVals) + 5, range = max - min || 1;
      const sisPoints = dataPoints.map((d, i) => `${padL + (i / (dataPoints.length - 1)) * chartW},${padT + (1 - (d.sistolica - min) / range) * chartH}`).join(' ');
      const diaPoints = dataPoints.map((d, i) => `${padL + (i / (dataPoints.length - 1)) * chartW},${padT + (1 - (d.diastolica - min) / range) * chartH}`).join(' ');
      lines = `<polyline points="${sisPoints}" fill="none" stroke="#ef4444" stroke-width="2"/><polyline points="${diaPoints}" fill="none" stroke="#2563eb" stroke-width="2"/>`;
      // Dots
      dataPoints.forEach((d, i) => {
        const x = padL + (i / (dataPoints.length - 1)) * chartW;
        lines += `<circle cx="${x}" cy="${padT + (1 - (d.sistolica - min) / range) * chartH}" r="3" fill="#ef4444"/>`;
        lines += `<circle cx="${x}" cy="${padT + (1 - (d.diastolica - min) / range) * chartH}" r="3" fill="#2563eb"/>`;
      });
      // Y axis labels
      const steps = 5;
      for (let i = 0; i <= steps; i++) {
        const v = Math.round(min + (i / steps) * range);
        const y = padT + (1 - i / steps) * chartH;
        lines += `<text x="${padL - 5}" y="${y + 3}" text-anchor="end" font-size="8" fill="#64748b">${v}</text>`;
        lines += `<line x1="${padL}" y1="${y}" x2="${padL + chartW}" y2="${y}" stroke="#e2e8f0" stroke-width="0.5"/>`;
      }
      lines += `<text x="${w - 10}" y="${padT - 5}" text-anchor="end" font-size="8" fill="#ef4444">Sistolica</text>`;
      lines += `<text x="${w - 10}" y="${padT + 7}" text-anchor="end" font-size="8" fill="#2563eb">Diastolica</text>`;
    } else {
      allVals = dataPoints.map(d => d.valore);
      const min = Math.min(...allVals) - (Math.max(...allVals) - Math.min(...allVals)) * 0.1;
      const max = Math.max(...allVals) + (Math.max(...allVals) - Math.min(...allVals)) * 0.1;
      const range = max - min || 1;
      const pts = dataPoints.map((d, i) => `${padL + (i / (dataPoints.length - 1)) * chartW},${padT + (1 - (d.valore - min) / range) * chartH}`).join(' ');
      lines = `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="2"/>`;
      dataPoints.forEach((d, i) => {
        const x = padL + (i / (dataPoints.length - 1)) * chartW;
        const y = padT + (1 - (d.valore - min) / range) * chartH;
        lines += `<circle cx="${x}" cy="${y}" r="3" fill="${color}"/>`;
      });
      const steps = 5;
      for (let i = 0; i <= steps; i++) {
        const v = (min + (i / steps) * range).toFixed(1);
        const y = padT + (1 - i / steps) * chartH;
        lines += `<text x="${padL - 5}" y="${y + 3}" text-anchor="end" font-size="8" fill="#64748b">${v}</text>`;
        lines += `<line x1="${padL}" y1="${y}" x2="${padL + chartW}" y2="${y}" stroke="#e2e8f0" stroke-width="0.5"/>`;
      }
    }
    // X axis labels
    const maxLabels = Math.min(dataPoints.length, 10);
    const step = Math.max(1, Math.floor(dataPoints.length / maxLabels));
    dataPoints.forEach((d, i) => {
      if (i % step === 0 || i === dataPoints.length - 1) {
        const x = padL + (i / (dataPoints.length - 1)) * chartW;
        lines += `<text x="${x}" y="${h - 5}" text-anchor="middle" font-size="7" fill="#64748b">${d.data}</text>`;
      }
    });
    // Border and title
    return `<div style="margin:12px 0;text-align:center"><p style="font-size:10px;font-weight:bold;color:${color};margin-bottom:4px">${label} (${unit})</p><svg width="${w}" height="${h}" style="border:1px solid #e2e8f0;border-radius:6px;background:#fafafa"><rect x="${padL}" y="${padT}" width="${chartW}" height="${chartH}" fill="none" stroke="#e2e8f0"/>${lines}</svg></div>`;
  };

  const handlePrintReport = (reportType) => {
    const nome = `${ospite?.cognome || ''} ${ospite?.nome || ''}`;
    const camera = `Camera ${ospite?.stanza || '-'}/${ospite?.letto || '-'}`;

    if (reportType === 'completo') {
      // Full vitals report
      let body = '';
      paramTypes.forEach(tipo => {
        const items = tipo === 'PA' ? parametri.filter(p => p.tipo === 'PA_sistolica' || p.tipo === 'PA_diastolica') : parametri.filter(p => p.tipo === tipo);
        if (items.length === 0) return;
        body += `<p class="section-title">${tipiParamFull[tipo] || tipo}</p>`;
        // Add chart
        const cd = getChartData(tipo);
        if (cd.length >= 2) {
          body += buildSvgChart(cd, chartColors[tipo] || '#2563eb', tipiParam[tipo] || tipo, unitaMap[tipo] || '', tipo === 'PA');
        }
        body += '<table><thead><tr><th>Data/Ora</th><th>Turno</th><th>Valore</th><th>Operatore</th><th>Note</th></tr></thead><tbody>';
        if (tipo === 'PA') {
          const sisList = items.filter(p => p.tipo === 'PA_sistolica').slice(0, 60);
          sisList.forEach(s => {
            const dia = parametri.find(p => p.tipo === 'PA_diastolica' && Math.abs(new Date(p.data_rilevazione) - new Date(s.data_rilevazione)) < 60000);
            const turno = getTurno(s.data_rilevazione);
            const fuori = s.fuori_range || (dia && dia.fuori_range);
            body += `<tr><td>${new Date(s.data_rilevazione).toLocaleString('it-IT')}</td><td>${turnoLabel[turno]?.split(' ')[0] || turno}</td><td style="${fuori ? 'color:red;font-weight:bold' : 'font-weight:bold'}">${s.valore}/${dia?.valore || '-'} mmHg</td><td>${s.operatore_nome || ''}</td><td>${s.note || ''}</td></tr>`;
          });
        } else {
          items.slice(0, 60).forEach(p => {
            const turno = getTurno(p.data_rilevazione);
            body += `<tr><td>${new Date(p.data_rilevazione).toLocaleString('it-IT')}</td><td>${turnoLabel[turno]?.split(' ')[0] || turno}</td><td style="${p.fuori_range ? 'color:red;font-weight:bold' : 'font-weight:bold'}">${p.valore} ${p.unita || ''}</td><td>${p.operatore_nome || ''}</td><td>${p.note || ''}</td></tr>`;
          });
        }
        body += '</tbody></table>';
      });
      openPrintWindow('Report Parametri Vitali', body, { subtitle: `${nome} - ${camera}` });

    } else {
      // Specific parameter report (e.g. quaderno glicemia)
      const tipo = reportType;
      const tipoLabel = tipiParamFull[tipo] || tipo;
      const items = tipo === 'PA' ? parametri.filter(p => p.tipo === 'PA_sistolica') : parametri.filter(p => p.tipo === tipo);
      if (items.length === 0) { toast.error('Nessun dato per questo parametro'); return; }

      let body = `<div style="padding:8px 12px;border:2px solid ${chartColors[tipo] || '#2563eb'};text-align:center;font-weight:bold;color:${chartColors[tipo] || '#2563eb'};margin-bottom:12px;border-radius:6px;font-size:13px">${tipoLabel.toUpperCase()}</div>`;

      // Summary stats
      const vals = items.map(p => p.valore);
      const avg = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const fuoriRange = items.filter(p => p.fuori_range).length;
      body += '<div class="info-grid" style="margin-bottom:12px">';
      body += `<div class="info-item"><span class="info-label">Rilevazioni totali</span><span class="info-value">${items.length}</span></div>`;
      body += `<div class="info-item"><span class="info-label">Media</span><span class="info-value">${avg} ${unitaMap[tipo] || ''}</span></div>`;
      body += `<div class="info-item"><span class="info-label">Min / Max</span><span class="info-value">${min} / ${max}</span></div>`;
      body += `<div class="info-item"><span class="info-label">Fuori range</span><span class="info-value" style="${fuoriRange > 0 ? 'color:red' : ''}">${fuoriRange}</span></div>`;
      body += '</div>';

      // Add chart
      const cd = getChartData(tipo);
      if (cd.length >= 2) {
        body += buildSvgChart(cd, chartColors[tipo] || '#2563eb', tipiParam[tipo] || tipo, unitaMap[tipo] || '', tipo === 'PA');
      }

      // Daily comparison table
      body += '<p class="section-title">Dettaglio per Turno</p>';
      body += '<table><thead><tr><th>Data</th><th>Mattina</th><th>Pomeriggio</th><th>Notte</th></tr></thead><tbody>';
      const byDate = {};
      items.forEach(p => {
        const d = p.data_rilevazione?.slice(0, 10) || '';
        const turno = getTurno(p.data_rilevazione);
        if (!byDate[d]) byDate[d] = {};
        if (tipo === 'PA') {
          const dia = parametri.find(pp => pp.tipo === 'PA_diastolica' && pp.data_rilevazione?.slice(0, 10) === d && getTurno(pp.data_rilevazione) === turno);
          byDate[d][turno] = `${p.valore}/${dia?.valore || '-'}`;
          if (p.fuori_range) byDate[d][`${turno}_alert`] = true;
        } else {
          byDate[d][turno] = `${p.valore}`;
          if (p.fuori_range) byDate[d][`${turno}_alert`] = true;
        }
      });
      Object.entries(byDate).sort(([a], [b]) => b.localeCompare(a)).slice(0, 30).forEach(([d, vals]) => {
        const dateLabel = new Date(d).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });
        body += `<tr><td style="font-weight:bold">${dateLabel}</td>`;
        ['mattina', 'pomeriggio', 'notte'].forEach(t => {
          const v = vals[t] || '-';
          const alert = vals[`${t}_alert`];
          body += `<td style="text-align:center;${alert ? 'color:red;font-weight:bold' : ''}">${v}</td>`;
        });
        body += '</tr>';
      });
      body += '</tbody></table>';

      const reportTitle = tipo === 'glicemia' ? 'Quaderno Glicemia' : tipo === 'PA' ? 'Monitoraggio Pressione Arteriosa' : `Report ${tipoLabel}`;
      openPrintWindow(reportTitle, body, { subtitle: `${nome} - ${camera}` });
    }
  };

  return (
    <div className="space-y-4" data-testid="parametri-tab">
      <TrendPesoCard ospiteId={ospiteId} />
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-700">Parametri Vitali</h3>
        <div className="flex gap-2 flex-wrap">
          {parametri.length >= 3 && <AnalisiParametriDialog ospiteId={ospiteId} ospite={ospite} />}
          {parametri.length > 0 && (
            <>
              <Select value="print" onValueChange={v => { if (v !== 'print') handlePrintReport(v); }}>
                <SelectTrigger className="h-8 w-auto text-xs" data-testid="print-parametri-btn">
                  <Printer className="w-3.5 h-3.5 mr-1" /><span>Stampa Report</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="print" disabled>Scegli report...</SelectItem>
                  <SelectItem value="completo">Report Completo</SelectItem>
                  {paramTypes.map(t => (
                    <SelectItem key={t} value={t}>{t === 'glicemia' ? 'Quaderno Glicemia' : t === 'PA' ? 'Report Pressione' : `Report ${tipiParam[t] || t}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          {hasRole('infermiere', 'coordinatore', 'medico', 'oss') && (
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-parametro-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-1" />Rileva</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuova Rilevazione</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Parametro</Label>
                  <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v, valore_testo: v === 'coscienza' ? 'A - Vigile' : v === 'ossigeno' ? 'Aria ambiente' : '' }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(tipiParam).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                </div>
                {form.tipo === 'PA' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Sistolica (mmHg) *</Label><Input data-testid="pa-sistolica" type="number" value={form.sistolica} onChange={e => setForm(f => ({ ...f, sistolica: e.target.value }))} placeholder="es. 120" /></div>
                    <div><Label className="text-xs">Diastolica (mmHg) *</Label><Input data-testid="pa-diastolica" type="number" value={form.diastolica} onChange={e => setForm(f => ({ ...f, diastolica: e.target.value }))} placeholder="es. 80" /></div>
                  </div>
                ) : form.tipo === 'alvo' ? (
                  <div>
                    <Label className="text-xs">Alvo *</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {ALVO_OPTIONS.map(opt => (
                        <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, valore_testo: opt }))}
                          className={`px-3 py-1.5 text-xs rounded-md border transition-all font-medium ${form.valore_testo === opt ? 'bg-lime-600 text-white border-lime-600' : 'bg-white text-slate-500 border-slate-200 hover:border-lime-400'}`}
                          data-testid={`alvo-opt-${opt.toLowerCase()}`}>{opt}</button>
                      ))}
                    </div>
                  </div>
                ) : form.tipo === 'diuresi' ? (
                  <div>
                    <Label className="text-xs">Diuresi *</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {DIURESI_OPTIONS.map(opt => (
                        <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, valore_testo: opt }))}
                          className={`px-3 py-1.5 text-xs rounded-md border transition-all font-medium ${form.valore_testo === opt ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-slate-500 border-slate-200 hover:border-cyan-400'}`}
                          data-testid={`diuresi-opt-${opt.toLowerCase().replace('/', '-')}`}>{opt}</button>
                      ))}
                    </div>
                  </div>
                ) : form.tipo === 'coscienza' ? (
                  <div>
                    <Label className="text-xs">Stato di coscienza (AVPU) *</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {COSCIENZA_OPTIONS.map(opt => (
                        <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, valore_testo: opt }))}
                          className={`px-3 py-1.5 text-xs rounded-md border transition-all font-medium ${form.valore_testo === opt ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-500 border-slate-200 hover:border-teal-400'}`}
                          data-testid={`coscienza-opt-${opt[0].toLowerCase()}`}>{opt}</button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1.5">Voce necessaria per il calcolo completo del NEWS2. Qualsiasi valore diverso da "A" vale 3 punti.</p>
                  </div>
                ) : form.tipo === 'ossigeno' ? (
                  <div>
                    <Label className="text-xs">Ossigeno supplementare *</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {OSSIGENO_OPTIONS.map(opt => (
                        <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, valore_testo: opt }))}
                          className={`px-3 py-1.5 text-xs rounded-md border transition-all font-medium ${form.valore_testo === opt ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-500 border-slate-200 hover:border-sky-400'}`}
                          data-testid={`ossigeno-opt-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{opt}</button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1.5">L'ossigeno supplementare aggiunge 2 punti al NEWS2.</p>
                  </div>
                ) : (
                  <div><Label className="text-xs">Valore *</Label><Input data-testid="parametro-valore" type="number" step="0.1" value={form.valore} onChange={e => setForm(f => ({ ...f, valore: e.target.value }))} /></div>
                )}
                <div><Label className="text-xs">Data/Ora</Label><Input type="datetime-local" value={form.data_rilevazione} onChange={e => setForm(f => ({ ...f, data_rilevazione: e.target.value }))} /></div>
                <div><Label className="text-xs">Note</Label><Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></div>
                <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button><Button data-testid="save-parametro" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">Salva</Button></div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Filtro per tipo */}
      {paramTypes.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-slate-500 font-medium">Filtra:</span>
          <button onClick={() => setFiltroTipo('tutti')} className={`px-2.5 py-1 text-[10px] rounded-full border transition-all ${filtroTipo === 'tutti' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`} data-testid="filtro-tipo-tutti">Tutti</button>
          {paramTypes.map(t => (
            <button key={t} onClick={() => { setFiltroTipo(t); setSelectedChart(t); }} className={`px-2.5 py-1 text-[10px] rounded-full border transition-all ${filtroTipo === t ? 'text-white border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`} style={filtroTipo === t ? { background: chartColors[t] || '#2563eb' } : {}} data-testid={`filtro-tipo-${t}`}>{tipiParam[t] || t}</button>
          ))}
        </div>
      )}

      {/* Vista mode selector */}
      {paramTypes.length > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-500 font-medium">Vista:</span>
          {[{ k: 'trend', l: 'Trend' }, { k: 'giornaliero', l: 'Giornaliero' }, { k: 'mensile', l: 'Mensile' }].map(v => (
            <button key={v.k} onClick={() => setVistaMode(v.k)} className={`px-2.5 py-1 text-[10px] rounded border transition-all ${vistaMode === v.k ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`} data-testid={`vista-${v.k}`}>{v.l}</button>
          ))}
        </div>
      )}

      {/* Chart selector (for Trend view) */}
      {paramTypes.length > 0 && vistaMode === 'trend' && (
        <div className="space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {paramTypes.map(t => (
              <button key={t} onClick={() => setSelectedChart(t)}
                className={`px-3 py-1 text-xs rounded-full border transition-all ${selectedChart === t ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                data-testid={`chart-btn-${t}`}>
                {tipiParam[t] || t}
              </button>
            ))}
          </div>
          {(() => {
            const cd = getChartData(selectedChart);
            if (cd.length < 2) return <p className="text-xs text-slate-400 text-center py-4">Dati insufficienti per il grafico</p>;
            return (
              <Card><CardContent className="p-4">
                <p className="text-xs font-medium text-slate-500 mb-2">{tipiParamFull[selectedChart] || selectedChart} - Trend</p>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={cd}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="data" tick={{ fontSize: 9, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#64748b' }} domain={selectedChart === 'SpO2' ? [85, 100] : ['auto', 'auto']} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                      {selectedChart === 'SpO2' && (
                        <>
                          <ReferenceArea y1={95} y2={100} fill="#dcfce7" fillOpacity={0.5} label={{ value: 'Normale', position: 'insideTopRight', fontSize: 9, fill: '#16a34a' }} />
                          <ReferenceArea y1={90} y2={95} fill="#fef9c3" fillOpacity={0.5} label={{ value: 'Attenzione', position: 'insideTopRight', fontSize: 9, fill: '#ca8a04' }} />
                          <ReferenceArea y1={85} y2={90} fill="#fecaca" fillOpacity={0.5} label={{ value: 'Critico', position: 'insideTopRight', fontSize: 9, fill: '#dc2626' }} />
                          <ReferenceLine y={95} stroke="#16a34a" strokeDasharray="4 4" strokeWidth={1} />
                          <ReferenceLine y={90} stroke="#dc2626" strokeDasharray="4 4" strokeWidth={1} />
                        </>
                      )}
                      {selectedChart === 'PA' ? (
                        <>
                          <Line type="monotone" dataKey="sistolica" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Sistolica" />
                          <Line type="monotone" dataKey="diastolica" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} name="Diastolica" />
                        </>
                      ) : (
                        <Line type="monotone" dataKey="valore" stroke={chartColors[selectedChart] || '#2563eb'} strokeWidth={2} dot={{ r: 3 }} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent></Card>
            );
          })()}
        </div>
      )}

      {/* Giornaliero view: bars by turno */}
      {paramTypes.length > 0 && vistaMode === 'giornaliero' && (
        <div className="space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {paramTypes.map(t => (
              <button key={t} onClick={() => setSelectedChart(t)}
                className={`px-3 py-1 text-xs rounded-full border transition-all ${selectedChart === t ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                {tipiParam[t] || t}
              </button>
            ))}
          </div>
          {(() => {
            const gd = getGiornalieroData(selectedChart);
            if (gd.length === 0) return <p className="text-xs text-slate-400 text-center py-4">Nessun dato</p>;
            return (
              <Card><CardContent className="p-4">
                <p className="text-xs font-medium text-slate-500 mb-1">{tipiParamFull[selectedChart] || selectedChart} - Confronto Turni</p>
                <div className="flex gap-3 mb-2">
                  {Object.entries(turnoLabel).map(([k, v]) => (
                    <span key={k} className="flex items-center gap-1 text-[10px] text-slate-500">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: turnoColors[k] }} />{v}
                    </span>
                  ))}
                </div>
                {selectedChart === 'PA' ? (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-[10px]">Data</TableHead>
                      <TableHead className="text-[10px]" style={{ color: turnoColors.mattina }}>Mattina</TableHead>
                      <TableHead className="text-[10px]" style={{ color: turnoColors.pomeriggio }}>Pomeriggio</TableHead>
                      <TableHead className="text-[10px]" style={{ color: turnoColors.notte }}>Notte</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {gd.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-medium">{r.data}</TableCell>
                          <TableCell className="text-xs font-mono">{r.sis_mattina ? `${r.sis_mattina}/${r.dia_mattina || '-'}` : '-'}</TableCell>
                          <TableCell className="text-xs font-mono">{r.sis_pomeriggio ? `${r.sis_pomeriggio}/${r.dia_pomeriggio || '-'}` : '-'}</TableCell>
                          <TableCell className="text-xs font-mono">{r.sis_notte ? `${r.sis_notte}/${r.dia_notte || '-'}` : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-[10px]">Data</TableHead>
                      <TableHead className="text-[10px]" style={{ color: turnoColors.mattina }}>Mattina</TableHead>
                      <TableHead className="text-[10px]" style={{ color: turnoColors.pomeriggio }}>Pomeriggio</TableHead>
                      <TableHead className="text-[10px]" style={{ color: turnoColors.notte }}>Notte</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {gd.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-medium">{r.data}</TableCell>
                          <TableCell className="text-xs font-mono">{r.mattina ?? '-'}</TableCell>
                          <TableCell className="text-xs font-mono">{r.pomeriggio ?? '-'}</TableCell>
                          <TableCell className="text-xs font-mono">{r.notte ?? '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent></Card>
            );
          })()}
        </div>
      )}

      {/* Mensile view */}
      {paramTypes.length > 0 && vistaMode === 'mensile' && (
        <div className="space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {paramTypes.map(t => (
              <button key={t} onClick={() => setSelectedChart(t)}
                className={`px-3 py-1 text-xs rounded-full border transition-all ${selectedChart === t ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                {tipiParam[t] || t}
              </button>
            ))}
          </div>
          {(() => {
            const md = getMensileData(selectedChart);
            if (md.length === 0) return <p className="text-xs text-slate-400 text-center py-4">Nessun dato</p>;
            return (
              <Card><CardContent className="p-4">
                <p className="text-xs font-medium text-slate-500 mb-2">{tipiParamFull[selectedChart] || selectedChart} - Medie Mensili</p>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-[10px]">Mese</TableHead>
                    <TableHead className="text-[10px]">Media</TableHead>
                    <TableHead className="text-[10px]">Min</TableHead>
                    <TableHead className="text-[10px]">Max</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {md.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs font-medium">{r.data}</TableCell>
                        {selectedChart === 'PA' ? (
                          <>
                            <TableCell className="text-xs font-mono font-bold">{r.sistolica}/{r.diastolica}</TableCell>
                            <TableCell className="text-xs text-slate-500">{r.min_sis}</TableCell>
                            <TableCell className="text-xs text-slate-500">{r.max_sis}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="text-xs font-mono font-bold">{r.valore} {unitaMap[selectedChart] || ''}</TableCell>
                            <TableCell className="text-xs text-slate-500">{r.min}</TableCell>
                            <TableCell className="text-xs text-slate-500">{r.max}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent></Card>
            );
          })()}
        </div>
      )}

      {/* Data table */}
      {filteredParametri.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Nessun parametro registrato</p> : (
        <Table>
          <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Valore</TableHead><TableHead>Turno</TableHead><TableHead>Data</TableHead><TableHead>Operatore</TableHead></TableRow></TableHeader>
          <TableBody>
            {filteredParametri.slice(0, 40).map(p => {
              const turno = getTurno(p.data_rilevazione);
              return (
                <TableRow key={p.id} className={p.fuori_range ? 'bg-red-50' : ''}>
                  <TableCell className="text-sm">{tipiParam[normTipo(p.tipo)] || p.tipo}</TableCell>
                  <TableCell className={`text-sm font-medium ${p.fuori_range ? 'text-red-600 font-bold' : ''}`}>{p.valore_testo ? p.valore_testo : `${p.valore} ${p.unita}`}</TableCell>
                  <TableCell><span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${turnoColors[turno]}20`, color: turnoColors[turno] }}>{turnoLabel[turno]?.split(' ')[0] || turno}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(p.data_rilevazione).toLocaleString('it-IT')}</TableCell>
                  <TableCell className="text-xs text-slate-500">{p.operatore_nome}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// === DIARIO TAB ===
function DiarioOspiteTab({ ospiteId }) {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ turno: 'mattino', data: new Date().toISOString().slice(0, 10), contenuto: '', tags: '' });
  const turnoMap = { mattino: 'turno-mattino', pomeriggio: 'turno-pomeriggio', notte: 'turno-notte' };

  const fetchEntries = useCallback(() => {
    api.get('/diario', { params: { ospite_id: ospiteId } }).then(r => setEntries(r.data)).catch(() => {});
  }, [ospiteId]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSave = async () => {
    if (!form.contenuto) { toast.error('Inserisci il contenuto'); return; }
    try {
      await api.post('/diario', { ...form, ospite_id: ospiteId, tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [] });
      toast.success('Nota aggiunta'); setShowForm(false); setForm({ turno: 'mattino', data: new Date().toISOString().slice(0, 10), contenuto: '', tags: '' }); fetchEntries();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4" data-testid="diario-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-slate-700">Diario Clinico</h3>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-diario-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-1" />Nuova Nota</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuova Nota Diario</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Turno</Label><Select value={form.turno} onValueChange={v => setForm(f => ({ ...f, turno: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mattino">Mattino</SelectItem><SelectItem value="pomeriggio">Pomeriggio</SelectItem><SelectItem value="notte">Notte</SelectItem></SelectContent></Select></div>
                <div><Label className="text-xs">Data</Label><DatePickerField value={form.data} onChange={v => setForm(f => ({ ...f, data: v }))} /></div>
              </div>
              <div><Label className="text-xs">Contenuto *</Label><DictableTextarea data-testid="diario-contenuto" value={form.contenuto} onChange={e => setForm(f => ({ ...f, contenuto: e.target.value }))} rows={4} /></div>
              <div><Label className="text-xs">Tag (separati da virgola)</Label><Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="alimentazione, mobilizzazione" /></div>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button><Button data-testid="save-diario" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">Salva</Button></div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {entries.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Nessuna nota</p> : (
        <div className="space-y-3">{entries.map(e => (
          <Card key={e.id}><CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`text-[10px] ${turnoMap[e.turno] || ''}`}>{e.turno}</Badge>
                  <span className="text-xs text-slate-400">{new Date(e.data).toLocaleDateString('it-IT')}</span>
                </div>
                <p className="text-sm text-slate-800">{e.contenuto}</p>
                {e.tags?.length > 0 && <div className="flex gap-1 mt-2">{e.tags.map((t, i) => <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>)}</div>}
              </div>
              <span className="text-xs text-slate-400 flex-shrink-0">{e.operatore_nome}</span>
            </div>
          </CardContent></Card>
        ))}</div>
      )}
    </div>
  );
}

// === EVENTI TAB ===
function EventiOspiteTab({ ospiteId }) {
  const [eventi, setEventi] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tipo: 'caduta', data_ora: new Date().toISOString().slice(0, 16), luogo: '', dinamica: '', conseguenze: '', interventi: '' });

  const fetchEventi = useCallback(() => {
    api.get('/eventi', { params: { ospite_id: ospiteId } }).then(r => setEventi(r.data)).catch(() => {});
  }, [ospiteId]);
  useEffect(() => { fetchEventi(); }, [fetchEventi]);

  const handleSave = async () => {
    if (!form.dinamica) { toast.error('Descrivi la dinamica'); return; }
    try {
      await api.post('/eventi', { ...form, ospite_id: ospiteId });
      toast.success('Evento registrato'); setShowForm(false); setForm({ tipo: 'caduta', data_ora: new Date().toISOString().slice(0, 16), luogo: '', dinamica: '', conseguenze: '', interventi: '' }); fetchEventi();
    } catch (e) { toast.error('Errore'); }
  };

  const tipoColors = { caduta: 'bg-red-100 text-red-700', lesione_da_pressione: 'bg-orange-100 text-orange-700', agitazione: 'bg-amber-100 text-amber-700', ricovero: 'bg-blue-100 text-blue-700', altro: 'bg-slate-100 text-slate-600' };

  return (
    <div className="space-y-4" data-testid="eventi-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-slate-700">Eventi Avversi</h3>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-evento-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-1" />Segnala</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Segnalazione Evento Avverso</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Tipo</Label><Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="caduta">Caduta</SelectItem><SelectItem value="lesione_da_pressione">Lesione da pressione</SelectItem><SelectItem value="agitazione">Agitazione</SelectItem><SelectItem value="ricovero">Ricovero</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent></Select></div>
                <div><Label className="text-xs">Data/Ora</Label><Input type="datetime-local" value={form.data_ora} onChange={e => setForm(f => ({ ...f, data_ora: e.target.value }))} /></div>
              </div>
              <div><Label className="text-xs">Luogo</Label><Input value={form.luogo} onChange={e => setForm(f => ({ ...f, luogo: e.target.value }))} placeholder="Camera, Bagno, Corridoio..." /></div>
              <div><Label className="text-xs">Dinamica *</Label><DictableTextarea data-testid="evento-dinamica" value={form.dinamica} onChange={e => setForm(f => ({ ...f, dinamica: e.target.value }))} rows={3} /></div>
              <div><Label className="text-xs">Conseguenze</Label><DictableTextarea value={form.conseguenze} onChange={e => setForm(f => ({ ...f, conseguenze: e.target.value }))} rows={2} /></div>
              <div><Label className="text-xs">Interventi Adottati</Label><DictableTextarea value={form.interventi} onChange={e => setForm(f => ({ ...f, interventi: e.target.value }))} rows={2} /></div>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button><Button data-testid="save-evento" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">Salva</Button></div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {eventi.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Nessun evento</p> : (
        <div className="space-y-3">{eventi.map(e => (
          <Card key={e.id}><CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`text-[10px] ${tipoColors[e.tipo] || ''}`}>{e.tipo?.replace(/_/g, ' ')}</Badge>
                  <span className="text-xs text-slate-400">{new Date(e.data_ora).toLocaleString('it-IT')}</span>
                  {e.luogo && <span className="text-xs text-slate-400">- {e.luogo}</span>}
                </div>
                <p className="text-sm text-slate-800">{e.dinamica}</p>
                {e.conseguenze && <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Conseguenze:</span> {e.conseguenze}</p>}
                {e.interventi && <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Interventi:</span> {e.interventi}</p>}
              </div>
              <span className="text-xs text-slate-400 flex-shrink-0">{e.operatore_nome}</span>
            </div>
          </CardContent></Card>
        ))}</div>
      )}
    </div>
  );
}

// === DOCUMENTI TAB ===
function DocumentiTab({ ospiteId }) {
  const { hasRole } = useAuth();
  const [documenti, setDocumenti] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ nome_file: '', tipo: 'referto', categoria: '', note: '' });
  const [fileData, setFileData] = useState(null);
  const tipiDoc = { referto: 'Referto', consenso: 'Consenso', lettera_dimissione: 'Lettera Dimissione', radiografia: 'Radiografia', esame_sangue: 'Esame del Sangue', altro: 'Altro' };

  const fetchDocs = useCallback(() => {
    api.get('/documenti', { params: { ospite_id: ospiteId } }).then(r => setDocumenti(r.data)).catch(() => {});
  }, [ospiteId]);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File troppo grande (max 5MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setFileData(reader.result);
      setForm(f => ({ ...f, nome_file: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!fileData || !form.nome_file) { toast.error('Seleziona un file'); return; }
    setUploading(true);
    try {
      await api.post('/documenti', { ...form, ospite_id: ospiteId, contenuto_base64: fileData });
      toast.success('Documento caricato');
      setShowForm(false);
      setForm({ nome_file: '', tipo: 'referto', categoria: '', note: '' });
      setFileData(null);
      fetchDocs();
    } catch (e) { toast.error('Errore upload'); }
    setUploading(false);
  };

  const handleDownload = async (docId, nomeFile) => {
    try {
      const res = await api.get(`/documenti/${docId}`);
      const link = document.createElement('a');
      link.href = res.data.contenuto_base64;
      link.download = nomeFile;
      link.click();
    } catch (e) { toast.error('Errore download'); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;
    try {
      await api.delete(`/documenti/${docId}`);
      toast.success('Documento eliminato');
      fetchDocs();
    } catch (e) { toast.error('Errore eliminazione'); }
  };

  return (
    <div className="space-y-4" data-testid="documenti-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-slate-700">Documenti e Allegati</h3>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-documento-btn" className="bg-brand hover:bg-brand-dark text-white"><Upload className="w-4 h-4 mr-1" />Carica</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Carica Documento</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">File (PDF, Immagini - max 5MB)</Label>
                <Input data-testid="doc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp" onChange={handleFileSelect} className="mt-1" />
                {form.nome_file && <p className="text-xs text-emerald-600 mt-1">File selezionato: {form.nome_file}</p>}
              </div>
              <div><Label className="text-xs">Tipo Documento</Label>
                <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(tipiDoc).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Categoria</Label><Input value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} placeholder="es. Cardiologia, Ortopedia..." /></div>
              <div><Label className="text-xs">Note</Label><DictableTextarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
                <Button data-testid="save-documento" onClick={handleUpload} disabled={uploading || !fileData} className="bg-brand hover:bg-brand-dark text-white">{uploading ? 'Caricamento...' : 'Carica'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {documenti.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Nessun documento</p> : (
        <div className="space-y-2">
          {documenti.map(d => (
            <Card key={d.id}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50"><Paperclip className="w-4 h-4 text-brand" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{d.nome_file}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[10px]">{tipiDoc[d.tipo] || d.tipo}</Badge>
                    {d.categoria && <span className="text-[10px] text-slate-400">{d.categoria}</span>}
                    <span className="text-[10px] text-slate-400">{d.uploaded_by_name} - {new Date(d.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDownload(d.id, d.nome_file)} data-testid={`download-doc-${d.id}`}><Download className="w-4 h-4 text-brand" /></Button>
                  {hasRole('coordinatore', 'amministrazione', 'medico') && (
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(d.id)} data-testid={`delete-doc-${d.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// === ALLERGIA ROW WITH AIFA SEARCH ===
function AllergiaRow({ allergia, index, allergie, setForm }) {
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/utility/categorie-farmacologiche').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const handleSostanzaChange = async (val) => {
    const na = [...allergie]; na[index] = { ...na[index], sostanza: val.toUpperCase() }; setForm(f => ({ ...f, allergie: na }));
    if (val.length >= 2) {
      setSearching(true);
      try {
        const res = await api.get(`/utility/cerca-farmaco-aifa?q=${encodeURIComponent(val)}`);
        setSearchResults(res.data);
      } catch { setSearchResults([]); }
      setSearching(false);
    } else { setSearchResults([]); }
  };

  const selectPA = (result) => {
    const na = [...allergie];
    na[index] = { ...na[index], sostanza: result.principio_attivo, categoria_atc: result.atc_prefix || '' };
    setForm(f => ({ ...f, allergie: na }));
    setSearchResults([]);
  };

  return (
    <div className="space-y-2 p-2.5 rounded-lg bg-red-50/50 border border-red-100">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="relative">
          <Label className="text-[10px]">Principio Attivo / Sostanza</Label>
          <Input value={allergia.sostanza} onChange={e => handleSostanzaChange(e.target.value)} placeholder="Cerca principio attivo..." className="text-xs" />
          {searchResults.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {searchResults.map((r, j) => (
                <button key={j} className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex justify-between" onClick={() => selectPA(r)}>
                  <span className="font-medium">{r.principio_attivo}</span>
                  {r.categoria && <span className="text-[10px] text-slate-400 ml-2">{r.categoria}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-[10px]">Categoria Farmacologica (ATC)</Label>
          <Select value={allergia.categoria_atc || ''} onValueChange={v => { const na = [...allergie]; na[index] = { ...na[index], categoria_atc: v === 'nessuna' ? '' : v }; setForm(f => ({ ...f, allergie: na })); }}>
            <SelectTrigger className="text-xs"><SelectValue placeholder="Seleziona categoria..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nessuna">Nessuna (solo sostanza)</SelectItem>
              {categories.map(c => <SelectItem key={c.atc} value={c.atc}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 items-end">
        <div><Label className="text-[10px]">Tipo di allergia</Label>
          <Select value={allergia.tipo || 'farmaco'} onValueChange={v => { const na = [...allergie]; na[index] = { ...na[index], tipo: v }; setForm(f => ({ ...f, allergie: na })); }}>
            <SelectTrigger className="text-xs" data-testid={`allergia-tipo-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="farmaco">Farmaco</SelectItem>
              <SelectItem value="alimento">Alimento</SelectItem>
              <SelectItem value="lattice">Lattice</SelectItem>
              <SelectItem value="contrasto">Mezzo di contrasto</SelectItem>
              <SelectItem value="cerotto">Cerotto / medicazione</SelectItem>
              <SelectItem value="altro">Altro</SelectItem>
            </SelectContent>
          </Select></div>
        <div><Label className="text-[10px]">Reazione</Label><Input value={allergia.reazione} onChange={e => { const na = [...allergie]; na[index] = { ...na[index], reazione: e.target.value }; setForm(f => ({ ...f, allergie: na })); }} placeholder="Es. Rash, edema..." className="text-xs" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2 items-end">
        <div><Label className="text-[10px]">Gravita</Label>
          <Select value={allergia.gravita} onValueChange={v => { const na = [...allergie]; na[index] = { ...na[index], gravita: v }; setForm(f => ({ ...f, allergie: na })); }}>
            <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="lieve">Lieve</SelectItem><SelectItem value="moderata">Moderata</SelectItem><SelectItem value="grave">Grave</SelectItem></SelectContent>
          </Select></div>
        <Button type="button" variant="ghost" size="sm" className="text-red-500 h-9" onClick={() => { if (!window.confirm('Rimuovere questa intolleranza?')) return; const na = allergie.filter((_, j) => j !== index); setForm(f => ({ ...f, allergie: na })); }}><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
      {allergia.categoria_atc && categories.find(c => c.atc === allergia.categoria_atc) && (
        <p className="text-[10px] text-red-600 bg-red-100 rounded px-2 py-1">
          Tutti i farmaci della classe <strong>{categories.find(c => c.atc === allergia.categoria_atc)?.nome}</strong> saranno segnalati alla prescrizione
        </p>
      )}
    </div>
  );
}


// === MAIN PAGE ===
export default function OspiteDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [ospite, setOspite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const tabRichiesta = searchParams.get('tab') || 'anagrafica';
  const [activeTab, setActiveTab] = useState(['lab', 'ecg'].includes(tabRichiesta) ? 'referti' : tabRichiesta);
  const [subReferti, setSubReferti] = useState(['lab', 'ecg'].includes(tabRichiesta) ? tabRichiesta : 'referti');
  const [showStatoDialog, setShowStatoDialog] = useState(false);
  const [statoForm, setStatoForm] = useState({ stato: '', motivo: '', data_evento: new Date().toISOString().slice(0, 10) });
  const [printingCartella, setPrintingCartella] = useState(false);

  const fetchOspite = useCallback(() => {
    Promise.all([
      api.get(`/ospiti/${id}`),
      api.get('/utility/atc-categories').catch(() => ({ data: {} }))
    ]).then(([r, atcRes]) => {
      const osp = r.data;
      const atcMap = atcRes.data || {};
      if (osp.allergie) {
        osp.allergie = osp.allergie.map(a => ({
          ...a,
          categoria_atc_nome: a.categoria_atc ? atcMap[a.categoria_atc] || a.categoria_atc : ''
        }));
      }
      setOspite(osp);
    }).catch(() => toast.error('Ospite non trovato')).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchOspite(); }, [fetchOspite]);

  const handlePrint = () => { window.print(); };

  const handleCambiaStato = async () => {
    if (!statoForm.stato) return;
    try {
      await api.put(`/ospiti/${id}/stato`, statoForm);
      toast.success(`Stato aggiornato: ${statusLabels[statoForm.stato]}`);
      setShowStatoDialog(false);
      fetchOspite();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleStampaCartella = async () => {
    setPrintingCartella(true);
    try {
      const res = await api.get(`/ospiti/${id}/cartella-completa`);
      const c = res.data;
      const o = c.ospite;
      let body = '';

      // Anagrafica
      body += `<p class="section-title">Dati Anagrafici</p>`;
      body += `<div class="info-grid">`;
      body += `<div class="info-item"><span class="info-label">Cognome e Nome</span><span class="info-value">${o.cognome} ${o.nome}</span></div>`;
      if (o.data_nascita) body += `<div class="info-item"><span class="info-label">Data Nascita</span><span class="info-value">${new Date(o.data_nascita).toLocaleDateString('it-IT')}</span></div>`;
      if (o.luogo_nascita) body += `<div class="info-item"><span class="info-label">Luogo Nascita</span><span class="info-value">${o.luogo_nascita}</span></div>`;
      if (o.codice_fiscale) body += `<div class="info-item"><span class="info-label">Codice Fiscale</span><span class="info-value" style="font-family:monospace">${o.codice_fiscale}</span></div>`;
      body += `<div class="info-item"><span class="info-label">Sesso</span><span class="info-value">${o.sesso === 'M' ? 'Maschio' : 'Femmina'}</span></div>`;
      body += `<div class="info-item"><span class="info-label">Camera / Letto</span><span class="info-value">${o.stanza || '-'}/${o.letto || '-'}</span></div>`;
      body += `<div class="info-item"><span class="info-label">Stato</span><span class="info-value">${statusLabels[o.stato] || o.stato}</span></div>`;
      body += `</div>`;

      // Diagnosi
      if (o.diagnosi?.length > 0) {
        body += `<p class="section-title">Diagnosi</p><ul>`;
        o.diagnosi.forEach(d => { body += `<li style="font-size:9px;margin-bottom:2px">${d.descrizione}${d.data ? ' (' + d.data + ')' : ''}</li>`; });
        body += `</ul>`;
      }

      // Allergie
      if (o.allergie?.length > 0) {
        body += `<p class="section-title">Allergie</p><ul>`;
        o.allergie.forEach(a => { body += `<li style="font-size:9px">${a.tipo}: ${a.descrizione}${a.gravita ? ' - ' + a.gravita : ''}</li>`; });
        body += `</ul>`;
      }

      // Terapie
      if (c.terapie.length > 0) {
        body += `<p class="section-title">Terapie (${c.terapie.length})</p>`;
        body += `<table><thead><tr><th>Farmaco</th><th>Dosaggio</th><th>Stato</th><th>Note</th></tr></thead><tbody>`;
        c.terapie.forEach(t => { body += `<tr><td>${t.farmaco}</td><td>${t.dosaggio || '-'}</td><td>${t.stato}</td><td style="font-size:8px">${t.note || ''}</td></tr>`; });
        body += `</tbody></table>`;
      }

      // PAI
      if (c.pai.length > 0) {
        const p = c.pai[0];
        body += `<p class="section-title">PAI</p>`;
        body += `<div class="info-grid">`;
        body += `<div class="info-item"><span class="info-label">Stato</span><span class="info-value">${p.stato}</span></div>`;
        body += `<div class="info-item"><span class="info-label">Frequenza Rivalutazione</span><span class="info-value">${p.frequenza_rivalutazione || '-'}</span></div>`;
        body += `<div class="info-item"><span class="info-label">Prossima Rivalutazione</span><span class="info-value">${p.data_prossima_rivalutazione ? new Date(p.data_prossima_rivalutazione).toLocaleDateString('it-IT') : '-'}</span></div>`;
        body += `</div>`;
        if (p.valutazioni) {
          body += `<p style="font-size:9px;font-weight:bold;margin-top:6px">Scale di Valutazione</p><div class="info-grid">`;
          Object.entries(p.valutazioni).forEach(([k, v]) => { if (v?.punteggio != null) body += `<div class="info-item"><span class="info-label">${k}</span><span class="info-value">${v.punteggio}</span></div>`; });
          body += `</div>`;
        }
      }

      // Parametri Vitali
      if (c.parametri_vitali.length > 0) {
        body += `<p class="section-title">Parametri Vitali (ultimi 20)</p>`;
        body += `<table><thead><tr><th>Data</th><th>PA</th><th>FC</th><th>SpO2</th><th>Temp</th><th>Glicemia</th></tr></thead><tbody>`;
        c.parametri_vitali.slice(0, 20).forEach(p => { body += `<tr><td>${new Date(p.data_rilevazione).toLocaleDateString('it-IT')}</td><td>${p.pressione_sistolica || '-'}/${p.pressione_diastolica || '-'}</td><td>${p.frequenza_cardiaca || '-'}</td><td>${p.saturazione || '-'}</td><td>${p.temperatura || '-'}</td><td>${p.glicemia || '-'}</td></tr>`; });
        body += `</tbody></table>`;
      }

      // Eventi Avversi
      if (c.eventi_avversi.length > 0) {
        body += `<p class="section-title">Eventi Avversi (${c.eventi_avversi.length})</p>`;
        body += `<table><thead><tr><th>Data</th><th>Tipo</th><th>Gravita</th><th>Descrizione</th></tr></thead><tbody>`;
        c.eventi_avversi.forEach(e => { body += `<tr><td>${new Date(e.data_evento).toLocaleDateString('it-IT')}</td><td>${e.tipo}</td><td>${e.gravita || '-'}</td><td style="font-size:8px">${e.descrizione || ''}</td></tr>`; });
        body += `</tbody></table>`;
      }

      // Referti
      if (c.referti.length > 0) {
        body += `<p class="section-title">Referti (${c.referti.length})</p>`;
        body += `<table><thead><tr><th>Data</th><th>Tipo</th><th>Titolo</th><th>Medico</th></tr></thead><tbody>`;
        c.referti.forEach(r => { body += `<tr><td>${new Date(r.data_referto).toLocaleDateString('it-IT')}</td><td>${r.tipo_label || r.tipo}</td><td>${r.titolo}</td><td style="font-size:8px">${r.medico_richiedente || '-'}</td></tr>`; });
        body += `</tbody></table>`;
      }

      // Diario
      if (c.diario_clinico.length > 0) {
        body += `<p class="section-title">Diario Clinico (ultime 30 annotazioni)</p>`;
        c.diario_clinico.slice(0, 30).forEach(d => {
          body += `<div style="margin-bottom:4px;padding:3px 0;border-bottom:1px solid #eee"><span style="font-size:8px;color:#888">${new Date(d.data).toLocaleDateString('it-IT')} - ${d.autore_nome || ''}</span><p style="font-size:9px;margin:2px 0">${d.contenuto || ''}</p></div>`;
        });
      }

      // Appuntamenti
      if (c.appuntamenti.length > 0) {
        body += `<p class="section-title">Appuntamenti (${c.appuntamenti.length})</p>`;
        body += `<table><thead><tr><th>Data</th><th>Tipo</th><th>Note</th></tr></thead><tbody>`;
        c.appuntamenti.forEach(a => { body += `<tr><td>${new Date(a.data_ora).toLocaleDateString('it-IT')}</td><td>${a.tipo || '-'}</td><td style="font-size:8px">${a.note || ''}</td></tr>`; });
        body += `</tbody></table>`;
      }

      body += `<p style="font-size:7px;color:#999;margin-top:16px;text-align:right">Generato il ${new Date(c.generato_il).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} da ${c.generato_da}</p>`;

      openPrintWindow('Cartella Clinica Completa', body, {
        subtitle: `${o.cognome} ${o.nome} - CF: ${o.codice_fiscale || 'N/D'}`,
        date: new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
      });
    } catch { toast.error('Errore generazione cartella'); }
    setPrintingCartella(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;
  if (!ospite) return <div className="text-center py-20"><p className="text-slate-500">Ospite non trovato</p></div>;

  const s = statusMap[ospite.stato] || 'bg-emerald-100 text-emerald-700';

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="ospite-dettaglio-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/ospiti')} data-testid="back-to-ospiti" className="no-print"><ArrowLeft className="w-4 h-4 mr-1" />Indietro</Button>
        <Separator orientation="vertical" className="h-6 no-print" />
        <Avatar className="w-12 h-12 flex-shrink-0">
          {ospite.foto_base64 && <AvatarImage src={ospite.foto_base64} alt={`${ospite.nome} ${ospite.cognome}`} />}
          <AvatarFallback className={`text-lg font-bold ${ospite.sesso === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
            {ospite.nome?.[0]}{ospite.cognome?.[0]}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-slate-900 font-[Cinzel]">{ospite.cognome} {ospite.nome}</h1>
            <Badge className={`text-xs ${s}`}>{ospite.stato}</Badge>
          </div>
          <p className="text-sm text-slate-500">Camera {ospite.stanza} - Letto {ospite.letto}{ospite.area ? ` | ${ospite.area === 'verde' ? 'Area Verde' : 'Area Gialla'}` : ''} | {ospite.sesso === 'M' ? 'Maschio' : 'Femmina'}, {ospite.data_nascita ? `${new Date().getFullYear() - new Date(ospite.data_nascita).getFullYear()} anni` : ''}</p>
        </div>
        <div className="flex gap-2 no-print flex-wrap">
          <QRCodeOspite ospite={ospite} />
          <EmergencyPrintButton ospiteId={id} />
          <Button variant="outline" size="sm" onClick={handleStampaCartella} disabled={printingCartella} data-testid="print-cartella-btn">
            <FileDown className="w-4 h-4 mr-1" />{printingCartella ? 'Generazione...' : 'Stampa Cartella'}
          </Button>
          {hasRole('coordinatore') && (
            <Button variant="outline" size="sm" onClick={() => { setStatoForm({ stato: ospite.stato || 'presente', motivo: '', data_evento: new Date().toISOString().slice(0, 10) }); setShowStatoDialog(true); }} data-testid="change-status-btn">
              <Archive className="w-4 h-4 mr-1" />Stato
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border w-full justify-start overflow-x-auto no-print" data-testid="ospite-tabs">
          <TabsTrigger value="anagrafica" className="text-xs gap-1.5"><User className="w-3.5 h-3.5" />Anagrafica</TabsTrigger>
          <TabsTrigger value="cartella" className="text-xs gap-1.5"><FileText className="w-3.5 h-3.5" />Cartella</TabsTrigger>
          <TabsTrigger value="scale" className="text-xs gap-1.5" data-testid="tab-scale"><ShieldAlert className="w-3.5 h-3.5" />Scale Valutative</TabsTrigger>
          <TabsTrigger value="pai" className="text-xs gap-1.5"><ClipboardList className="w-3.5 h-3.5" />PAI</TabsTrigger>
          <TabsTrigger value="terapie" className="text-xs gap-1.5"><Pill className="w-3.5 h-3.5" />Terapie</TabsTrigger>
          <TabsTrigger value="parametri" className="text-xs gap-1.5"><Activity className="w-3.5 h-3.5" />Parametri</TabsTrigger>
          <TabsTrigger value="referti" className="text-xs gap-1.5" data-testid="tab-referti"><FlaskConical className="w-3.5 h-3.5" />Referti</TabsTrigger>
          <TabsTrigger value="nutrizione" className="text-xs gap-1.5" data-testid="tab-nutrizione"><Apple className="w-3.5 h-3.5" />Nutrizione</TabsTrigger>
          <TabsTrigger value="diario" className="text-xs gap-1.5"><BookOpen className="w-3.5 h-3.5" />Diario</TabsTrigger>
          <TabsTrigger value="eventi" className="text-xs gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Eventi</TabsTrigger>
          <TabsTrigger value="documenti" className="text-xs gap-1.5"><Paperclip className="w-3.5 h-3.5" />Documenti</TabsTrigger>
          <TabsTrigger value="appuntamenti" className="text-xs gap-1.5"><CalendarCheck className="w-3.5 h-3.5" />Appuntamenti</TabsTrigger>
          <TabsTrigger value="contratto" className="text-xs gap-1.5"><FileText className="w-3.5 h-3.5" />Contratto</TabsTrigger>
        </TabsList>
        <TabsContent value="anagrafica" className="mt-4"><AnagraficaTab ospite={ospite} onUpdate={fetchOspite} /></TabsContent>
        <TabsContent value="cartella" className="mt-4"><CartellaTab ospiteId={id} /></TabsContent>
        <TabsContent value="pai" className="mt-4"><PAITab ospiteId={id} ospite={ospite} /></TabsContent>
        <TabsContent value="terapie" className="mt-4"><TerapieOspiteTab ospiteId={id} /></TabsContent>
        <TabsContent value="parametri" className="mt-4"><ParametriTab ospiteId={id} ospite={ospite} /></TabsContent>
        <TabsContent value="scale" className="mt-4"><ScaleTab ospiteId={id} ospite={ospite} /></TabsContent>
        <TabsContent value="nutrizione" className="mt-4"><NutrizioneTab ospiteId={id} ospite={ospite} /></TabsContent>
        <TabsContent value="diario" className="mt-4"><DiarioOspiteTab ospiteId={id} /></TabsContent>
        <TabsContent value="eventi" className="mt-4"><EventiOspiteTab ospiteId={id} /></TabsContent>
        <TabsContent value="documenti" className="mt-4"><DocumentiTab ospiteId={id} /></TabsContent>
        <TabsContent value="referti" className="mt-4">
          <Tabs value={subReferti} onValueChange={setSubReferti} className="w-full">
            <TabsList className="bg-slate-100 no-print" data-testid="referti-subtabs">
              <TabsTrigger value="referti" className="text-xs gap-1.5" data-testid="subtab-referti"><FileText className="w-3.5 h-3.5" />Referti</TabsTrigger>
              <TabsTrigger value="lab" className="text-xs gap-1.5" data-testid="subtab-lab"><FlaskConical className="w-3.5 h-3.5" />Laboratorio</TabsTrigger>
              <TabsTrigger value="ecg" className="text-xs gap-1.5" data-testid="subtab-ecg"><HeartPulse className="w-3.5 h-3.5" />ECG</TabsTrigger>
            </TabsList>
            <TabsContent value="referti" className="mt-4"><RefertiTab ospiteId={id} /></TabsContent>
            <TabsContent value="lab" className="mt-4"><LabTab ospiteId={id} ospite={ospite} /></TabsContent>
            <TabsContent value="ecg" className="mt-4"><EcgTab ospiteId={id} ospite={ospite} /></TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="appuntamenti" className="mt-4"><AppuntamentiOspiteTab ospiteId={id} /></TabsContent>
        <TabsContent value="contratto" className="mt-4"><ContrattoTab ospite={ospite} /></TabsContent>
      </Tabs>

      {/* Stato Dialog */}
      <Dialog open={showStatoDialog} onOpenChange={setShowStatoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Archive className="w-5 h-5 text-brand" />Modifica Stato Ospite</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Stato</Label>
              <Select value={statoForm.stato} onValueChange={v => setStatoForm(f => ({ ...f, stato: v }))}>
                <SelectTrigger data-testid="stato-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presente">Presente</SelectItem>
                  <SelectItem value="ricoverato">Ricoverato (in ospedale)</SelectItem>
                  <SelectItem value="dimesso">Dimesso</SelectItem>
                  <SelectItem value="deceduto">Deceduto</SelectItem>
                  <SelectItem value="trasferito">Trasferito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {['dimesso', 'deceduto', 'trasferito'].includes(statoForm.stato) && (
              <>
                <div><Label className="text-xs">Data Evento</Label><DatePickerField value={statoForm.data_evento} onChange={v => setStatoForm(f => ({ ...f, data_evento: v }))} /></div>
                <div><Label className="text-xs">Motivo / Note</Label><Input value={statoForm.motivo} onChange={e => setStatoForm(f => ({ ...f, motivo: e.target.value }))} placeholder="Motivo dimissione/trasferimento..." /></div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-700 font-medium">La cartella verra archiviata automaticamente.</p>
                  <p className="text-xs text-amber-600 mt-1">Potrai comunque accedervi e stamparla in seguito.</p>
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowStatoDialog(false)}>Annulla</Button>
              <Button onClick={handleCambiaStato} className={['dimesso', 'deceduto', 'trasferito'].includes(statoForm.stato) ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-brand hover:bg-brand-dark text-white'} data-testid="save-stato-btn">
                {['dimesso', 'deceduto', 'trasferito'].includes(statoForm.stato) ? 'Archivia e Salva' : 'Salva Stato'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
